import { type CSSProperties, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Bot,
  BookOpen,
  ChevronDown,
  Download,
  Heart,
  Info,
  LogIn,
  LogOut,
  Mail,
  Minus,
  Plus,
  Save,
  Search,
  SendHorizontal,
  Shield,
  Sparkles,
  Sword,
  Trash2,
  Upload,
  UserRoundPlus,
  Users,
  X,
} from 'lucide-react';
import type { ReactNode } from 'react';
import {
  type AuthSession,
  type NewPasswordChallenge,
  authConfig,
  confirmPasswordReset,
  completeNewPassword,
  getStoredSession,
  requestPasswordReset,
  signIn,
  signOut,
} from './auth';

type AbilityKey = 'str' | 'dex' | 'con' | 'int' | 'wis' | 'cha';
type SheetTab = 'stats' | 'combat' | 'story' | 'inventory' | 'features' | 'party';
type ViewMode = 'library' | 'sheet' | 'dm';
type AuthMode = 'signIn' | 'resetRequest' | 'resetConfirm';

type Ability = { key: AbilityKey; name: string; score: number };
type Skill = { name: string; ability: AbilityKey; proficient: boolean; expertise?: boolean };
type Weapon = { name: string; bonus: string; damage: string };

type Character = {
  id: string;
  name: string;
  player: string;
  classLevel: string;
  background: string;
  race: string;
  alignment: string;
  xp: string;
  inspiration: boolean;
  proficiencyBonus: number;
  armorClass: number;
  initiative: number;
  speed: number;
  hpMax: number;
  hpCurrent: number;
  hpTemp: number;
  hitDice: string;
  deathSuccesses: number;
  deathFailures: number;
  passivePerception: number;
  savingThrowProficiencies?: Partial<Record<AbilityKey, boolean>>;
  savingThrowOverrides?: Partial<Record<AbilityKey, string>>;
  abilities: Ability[];
  skills: Skill[];
  skillOverrides?: Record<string, string>;
  weapons: Weapon[];
  attackNotes: string;
  traits: string;
  ideals: string;
  bonds: string;
  flaws: string;
  features: string;
  proficiencies: string;
  equipment: string;
  backstory: string;
  allies: string;
  treasure: string;
  appearance: string;
  portraitDataUrl?: string;
  personalNotes?: Record<string, string>;
  updatedAt: string;
};

type ReferenceEntry = { key: string; title: string; body: string };
type GroupMember = Pick<Character, 'id' | 'name' | 'player' | 'classLevel' | 'race' | 'alignment' | 'armorClass' | 'hpCurrent' | 'hpMax' | 'portraitDataUrl' | 'updatedAt'> & {
  groupMemberId?: string;
};
type ChatRole = 'user' | 'assistant';
type CharacterPatch = Partial<Character>;
type BuilderChoice = {
  id: string;
  label: string;
  question: string;
  recommendation?: string;
  options: string[];
  allowCustom?: boolean;
  customLabel?: string;
};
type ChatMessage = {
  role: ChatRole;
  content: string;
  sources?: string[];
  patch?: CharacterPatch | null;
  builderChoices?: BuilderChoice[];
};
type ChatSession = {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: string;
  updatedAt: string;
};
type ChatBudget = {
  modelId: string;
  limitMicroUsd: number;
  usedMicroUsd: number;
  actualMicroUsd: number;
  remainingMicroUsd: number;
};

type ClassRule = {
  id: string;
  label: string;
  aliases?: string[];
  hitDie: string;
  savingThrows: AbilityKey[];
  proficiencies: string;
  suggestedWeapons?: Weapon[];
  subclassLevel: number;
  subclassExamples: string[];
};

type RaceRule = {
  id: string;
  label: string;
  aliases?: string[];
  speed: number;
  traits: string;
  languages: string;
};

type BackgroundRule = {
  id: string;
  label: string;
  aliases?: string[];
  proficiencies: string;
  equipment: string;
};

type AlignmentRule = {
  id: string;
  label: string;
};

const groupApiUrl =
  (import.meta.env.VITE_GROUP_API_URL as string | undefined) ??
  'https://dtsrtdpsh3ex55djvc4suntc5y0nywso.lambda-url.us-east-1.on.aws';

const abilityLabels: Record<AbilityKey, string> = {
  str: 'Strength',
  dex: 'Dexterity',
  con: 'Constitution',
  int: 'Intelligence',
  wis: 'Wisdom',
  cha: 'Charisma',
};
const abilityKeys = Object.keys(abilityLabels) as AbilityKey[];

const classRules: ClassRule[] = [
  {
    id: 'fighter',
    label: 'Fighter',
    hitDie: 'd10',
    savingThrows: ['str', 'con'],
    proficiencies: 'Armor: All armor, shields\nWeapons: Simple weapons, martial weapons',
    suggestedWeapons: [
      { name: 'Longsword', bonus: '', damage: '1d8 slashing' },
      { name: 'Longbow', bonus: '', damage: '1d8 piercing' },
      { name: '', bonus: '', damage: '' },
    ],
    subclassLevel: 3,
    subclassExamples: ['Champion', 'Battle Master', 'Eldritch Knight'],
  },
  {
    id: 'rogue',
    label: 'Rogue',
    aliases: ['rouge'],
    hitDie: 'd8',
    savingThrows: ['dex', 'int'],
    proficiencies:
      'Armor: Light armor\nWeapons: Simple weapons, hand crossbows, longswords, rapiers, shortswords\nTools: Thieves tools',
    suggestedWeapons: [
      { name: 'Rapier', bonus: '', damage: '1d8 piercing' },
      { name: 'Shortbow', bonus: '', damage: '1d6 piercing' },
      { name: 'Dagger', bonus: '', damage: '1d4 piercing' },
    ],
    subclassLevel: 3,
    subclassExamples: ['Thief', 'Assassin', 'Arcane Trickster', 'Swashbuckler'],
  },
  {
    id: 'cleric',
    label: 'Cleric',
    hitDie: 'd8',
    savingThrows: ['wis', 'cha'],
    proficiencies: 'Armor: Light armor, medium armor, shields\nWeapons: Simple weapons',
    suggestedWeapons: [
      { name: 'Mace', bonus: '', damage: '1d6 bludgeoning' },
      { name: 'Light Crossbow', bonus: '', damage: '1d8 piercing' },
      { name: '', bonus: '', damage: '' },
    ],
    subclassLevel: 1,
    subclassExamples: ['Life Domain', 'Light Domain', 'Trickery Domain', 'War Domain'],
  },
  {
    id: 'wizard',
    label: 'Wizard',
    hitDie: 'd6',
    savingThrows: ['int', 'wis'],
    proficiencies: 'Weapons: Daggers, darts, slings, quarterstaffs, light crossbows',
    suggestedWeapons: [
      { name: 'Quarterstaff', bonus: '', damage: '1d6 bludgeoning' },
      { name: 'Light Crossbow', bonus: '', damage: '1d8 piercing' },
      { name: '', bonus: '', damage: '' },
    ],
    subclassLevel: 2,
    subclassExamples: ['Evocation', 'Divination', 'Illusion', 'Necromancy'],
  },
];

const raceRules: RaceRule[] = [
  {
    id: 'dragonborn',
    label: 'Dragonborn',
    speed: 30,
    traits: 'Draconic Ancestry\nBreath Weapon\nDamage Resistance',
    languages: 'Common, Draconic',
  },
  {
    id: 'hill-dwarf',
    label: 'Hill Dwarf',
    speed: 25,
    traits: 'Darkvision\nDwarven Resilience\nDwarven Toughness\nStonecunning',
    languages: 'Common, Dwarvish',
  },
  {
    id: 'mountain-dwarf',
    label: 'Mountain Dwarf',
    speed: 25,
    traits: 'Darkvision\nDwarven Resilience\nDwarven Armor Training\nStonecunning',
    languages: 'Common, Dwarvish',
  },
  {
    id: 'high-elf',
    label: 'High Elf',
    speed: 30,
    traits: 'Darkvision\nFey Ancestry\nTrance\nExtra wizard cantrip',
    languages: 'Common, Elvish, one extra language',
  },
  {
    id: 'wood-elf',
    label: 'Wood Elf',
    speed: 35,
    traits: 'Darkvision\nFey Ancestry\nTrance\nMask of the Wild',
    languages: 'Common, Elvish',
  },
  {
    id: 'drow',
    label: 'Drow',
    aliases: ['Dark Elf'],
    speed: 30,
    traits: 'Superior Darkvision\nFey Ancestry\nTrance\nSunlight Sensitivity\nDrow Magic\nDrow Weapon Training',
    languages: 'Common, Elvish',
  },
  {
    id: 'forest-gnome',
    label: 'Forest Gnome',
    speed: 25,
    traits: 'Darkvision\nGnome Cunning\nNatural Illusionist\nSpeak with Small Beasts',
    languages: 'Common, Gnomish',
  },
  {
    id: 'rock-gnome',
    label: 'Rock Gnome',
    speed: 25,
    traits: "Darkvision\nGnome Cunning\nArtificer's Lore\nTinker",
    languages: 'Common, Gnomish',
  },
  {
    id: 'half-elf',
    label: 'Half-Elf',
    aliases: ['Half Elf'],
    speed: 30,
    traits: 'Darkvision\nFey Ancestry\nSkill Versatility\nChoose two ability scores to increase by 1',
    languages: 'Common, Elvish, one extra language',
  },
  {
    id: 'half-orc',
    label: 'Half-Orc',
    aliases: ['Half Orc'],
    speed: 30,
    traits: 'Darkvision\nMenacing\nRelentless Endurance\nSavage Attacks',
    languages: 'Common, Orc',
  },
  {
    id: 'human',
    label: 'Human',
    speed: 30,
    traits: 'Ability scores increase broadly; confirm standard human or variant human with the DM',
    languages: 'Common, one extra language',
  },
  {
    id: 'variant-human',
    label: 'Variant Human',
    speed: 30,
    traits: 'Two ability scores increase by 1\nOne skill proficiency\nOne feat if allowed by the DM',
    languages: 'Common, one extra language',
  },
  {
    id: 'lightfoot-halfling',
    label: 'Lightfoot Halfling',
    speed: 25,
    traits: 'Lucky\nBrave\nHalfling Nimbleness\nNaturally Stealthy',
    languages: 'Common, Halfling',
  },
  {
    id: 'stout-halfling',
    label: 'Stout Halfling',
    speed: 25,
    traits: 'Lucky\nBrave\nHalfling Nimbleness\nStout Resilience',
    languages: 'Common, Halfling',
  },
  {
    id: 'tiefling',
    label: 'Tiefling',
    speed: 30,
    traits: 'Darkvision\nHellish Resistance\nInfernal Legacy',
    languages: 'Common, Infernal',
  },
];

const backgroundRules: BackgroundRule[] = [
  {
    id: 'criminal',
    label: 'Criminal',
    proficiencies: 'Skills: Deception, Stealth\nTools: One gaming set, thieves tools',
    equipment: 'Crowbar\nDark common clothes with a hood\n15 gp',
  },
  {
    id: 'soldier',
    label: 'Soldier',
    proficiencies: 'Skills: Athletics, Intimidation\nTools: One gaming set, vehicles (land)',
    equipment: 'Insignia of rank\nTrophy from a fallen enemy\nBone dice or deck of cards\nCommon clothes\n10 gp',
  },
  {
    id: 'acolyte',
    label: 'Acolyte',
    proficiencies: 'Skills: Insight, Religion\nLanguages: Two of your choice',
    equipment: 'Holy symbol\nPrayer book or prayer wheel\nIncense\nVestments\nCommon clothes\n15 gp',
  },
  {
    id: 'sage',
    label: 'Sage',
    proficiencies: 'Skills: Arcana, History\nLanguages: Two of your choice',
    equipment: 'Bottle of black ink\nQuill\nSmall knife\nLetter from a dead colleague\nCommon clothes\n10 gp',
  },
  {
    id: 'noble',
    label: 'Noble',
    proficiencies: 'Skills: History, Persuasion\nTools: One gaming set\nLanguages: One of your choice',
    equipment: 'Fine clothes\nSignet ring\nScroll of pedigree\n25 gp',
  },
  {
    id: 'entertainer',
    label: 'Entertainer',
    proficiencies: 'Skills: Acrobatics, Performance\nTools: Disguise kit, one musical instrument',
    equipment: 'Musical instrument\nFavor from an admirer\nCostume\n15 gp',
  },
];

const alignmentRules: AlignmentRule[] = [
  { id: 'lawful-good', label: 'Lawful Good' },
  { id: 'neutral-good', label: 'Neutral Good' },
  { id: 'chaotic-good', label: 'Chaotic Good' },
  { id: 'lawful-neutral', label: 'Lawful Neutral' },
  { id: 'true-neutral', label: 'True Neutral' },
  { id: 'chaotic-neutral', label: 'Chaotic Neutral' },
  { id: 'lawful-evil', label: 'Lawful Evil' },
  { id: 'neutral-evil', label: 'Neutral Evil' },
  { id: 'chaotic-evil', label: 'Chaotic Evil' },
];

const levelOptions = Array.from({ length: 20 }, (_, index) => index + 1);
const xpThresholdsByLevel = [
  0, 0, 300, 900, 2700, 6500, 14000, 23000, 34000, 48000, 64000, 85000, 100000, 120000, 140000, 165000, 195000,
  225000, 265000, 305000, 355000,
];
const managedRulesHeadings = [
  'Class Proficiencies',
  'Race Traits',
  'Race Languages',
  'Background Proficiencies',
  'Background Equipment',
];
const manualChoiceValue = '__manual__';

const blankAbilities: Ability[] = [
  { key: 'str', name: 'Strength', score: 10 },
  { key: 'dex', name: 'Dexterity', score: 10 },
  { key: 'con', name: 'Constitution', score: 10 },
  { key: 'int', name: 'Intelligence', score: 10 },
  { key: 'wis', name: 'Wisdom', score: 10 },
  { key: 'cha', name: 'Charisma', score: 10 },
];

const blankSkills: Skill[] = [
  { name: 'Acrobatics', ability: 'dex', proficient: false },
  { name: 'Animal Handling', ability: 'wis', proficient: false },
  { name: 'Arcana', ability: 'int', proficient: false },
  { name: 'Athletics', ability: 'str', proficient: false },
  { name: 'Deception', ability: 'cha', proficient: false },
  { name: 'History', ability: 'int', proficient: false },
  { name: 'Insight', ability: 'wis', proficient: false },
  { name: 'Intimidation', ability: 'cha', proficient: false },
  { name: 'Investigation', ability: 'int', proficient: false },
  { name: 'Medicine', ability: 'wis', proficient: false },
  { name: 'Nature', ability: 'int', proficient: false },
  { name: 'Perception', ability: 'wis', proficient: false },
  { name: 'Performance', ability: 'cha', proficient: false },
  { name: 'Persuasion', ability: 'cha', proficient: false },
  { name: 'Religion', ability: 'int', proficient: false },
  { name: 'Sleight of Hand', ability: 'dex', proficient: false },
  { name: 'Stealth', ability: 'dex', proficient: false },
  { name: 'Survival', ability: 'wis', proficient: false },
];

const blankWeapons: Weapon[] = [
  { name: '', bonus: '', damage: '' },
  { name: '', bonus: '', damage: '' },
  { name: '', bonus: '', damage: '' },
];

const defaultCharacter: Character = {
  id: 'wesmire',
  name: 'Wesmire Willowwood',
  player: 'Nolan',
  classLevel: 'Rogue Swashbuckler 5',
  background: 'Criminal',
  race: 'Wood Elf',
  alignment: 'Chaotic Neutral',
  xp: '6,500',
  inspiration: false,
  proficiencyBonus: 3,
  armorClass: 16,
  initiative: 7,
  speed: 35,
  hpMax: 40,
  hpCurrent: 40,
  hpTemp: 0,
  hitDice: '5d8',
  deathSuccesses: 0,
  deathFailures: 0,
  passivePerception: 13,
  savingThrowProficiencies: {
    dex: true,
    int: true,
  },
  savingThrowOverrides: {},
  abilities: [
    { key: 'str', name: 'Strength', score: 8 },
    { key: 'dex', name: 'Dexterity', score: 18 },
    { key: 'con', name: 'Constitution', score: 14 },
    { key: 'int', name: 'Intelligence', score: 14 },
    { key: 'wis', name: 'Wisdom', score: 10 },
    { key: 'cha', name: 'Charisma', score: 12 },
  ],
  skills: [
    { name: 'Acrobatics', ability: 'dex', proficient: true },
    { name: 'Animal Handling', ability: 'wis', proficient: false },
    { name: 'Arcana', ability: 'int', proficient: false },
    { name: 'Athletics', ability: 'str', proficient: false },
    { name: 'Deception', ability: 'cha', proficient: true },
    { name: 'History', ability: 'int', proficient: false },
    { name: 'Insight', ability: 'wis', proficient: true },
    { name: 'Intimidation', ability: 'cha', proficient: false },
    { name: 'Investigation', ability: 'int', proficient: true },
    { name: 'Medicine', ability: 'wis', proficient: false },
    { name: 'Nature', ability: 'int', proficient: false },
    { name: 'Perception', ability: 'wis', proficient: true },
    { name: 'Performance', ability: 'cha', proficient: false },
    { name: 'Persuasion', ability: 'cha', proficient: false },
    { name: 'Religion', ability: 'int', proficient: false },
    { name: 'Sleight of Hand', ability: 'dex', proficient: true, expertise: true },
    { name: 'Stealth', ability: 'dex', proficient: true, expertise: true },
    { name: 'Survival', ability: 'wis', proficient: false },
  ],
  skillOverrides: {},
  weapons: [
    { name: 'Rapier', bonus: '+7', damage: '1d8 + 4 piercing' },
    { name: 'Hand Crossbow', bonus: '+7', damage: '1d6 + 4 piercing' },
    { name: 'Dagger', bonus: '+7', damage: '1d4 + 4 piercing' },
  ],
  attackNotes: '',
  traits: 'I always have a plan for when things go wrong.\nI stay calm under pressure.',
  ideals: 'Chains are meant to be broken, as are those who forge them.',
  bonds: 'Someone I loved died because of a mistake I made. That will never happen again.',
  flaws: 'I turn tail and run when things look bad.',
  features:
    'Shadow Touched: Invisibility and Disguise Self once per day.\nElven Accuracy: Reroll one attack roll when you have advantage.\nCunning Action: Dash, Hide, or Disengage as a bonus action.\nUncanny Dodge: Use a reaction to halve damage from one attack.',
  proficiencies:
    'Languages: Common, Elvish, Thieves Cant\nArmor: Light armor\nWeapons: Simple weapons, hand crossbows, longswords, rapiers, shortswords\nTools: Thieves tools',
  equipment:
    'Studded leather armor\nRapier\nHand crossbow\nDagger\nBurglars pack\nThieves tools\n200 gp',
  backstory:
    'A careful wanderer with old debts, useful contacts, and a habit of keeping one exit in sight.',
  allies:
    'Tavern contacts, a few quiet smugglers, and one retired guard who still owes a favor.',
  treasure: '200 gp, a sealed letter, and a silver token from a forgotten guild.',
  appearance: 'Lean wood elf with traveling leathers, careful eyes, and a dark green cloak.',
  updatedAt: new Date().toISOString(),
};

const storageKey = 'dnd-character-org-characters-v2';
const oldStorageKey = 'dnd-character-org-sheet-v1';
const dmNotesStorageKey = 'dnd-character-org-dm-notes-v1';
const chatHistoryStorageKey = 'dnd-character-org-ai-chat-history-v1';
const maxChatSessions = 20;
const maxChatMessages = 80;

function createBlankCharacter(): Character {
  return {
    ...defaultCharacter,
    id: crypto.randomUUID(),
    name: 'New Adventurer',
    player: '',
    classLevel: 'Level 1',
    background: '',
    race: '',
    alignment: '',
    xp: '0',
    proficiencyBonus: 2,
    armorClass: 10,
    initiative: 0,
    speed: 30,
    hpMax: 0,
    hpCurrent: 0,
    hpTemp: 0,
    hitDice: '',
    deathSuccesses: 0,
    deathFailures: 0,
    passivePerception: 10,
    savingThrowProficiencies: {},
    savingThrowOverrides: {},
    abilities: blankAbilities.map((ability) => ({ ...ability })),
    skills: blankSkills.map((skill) => ({ ...skill })),
    skillOverrides: {},
    weapons: blankWeapons.map((weapon) => ({ ...weapon })),
    attackNotes: '',
    traits: '',
    ideals: '',
    bonds: '',
    flaws: '',
    features: '',
    proficiencies: '',
    equipment: '',
    backstory: '',
    allies: '',
    treasure: '',
    appearance: '',
    portraitDataUrl: undefined,
    personalNotes: {},
    updatedAt: new Date().toISOString(),
  };
}

function normalizeChoice(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function optionMatchesValue(option: { label: string; aliases?: string[] }, value: string) {
  const normalizedValue = normalizeChoice(value);
  const options = [option.label, ...(option.aliases ?? [])].map(normalizeChoice);
  return options.some((entry) => entry === normalizedValue);
}

function classRuleFromClassLevel(value: string) {
  const normalizedValue = normalizeChoice(value);
  return classRules.find((rule) =>
    [rule.label, ...(rule.aliases ?? [])].map(normalizeChoice).some((entry) => normalizedValue.includes(entry))
  );
}

function choiceRuleFromValue<T extends { label: string; aliases?: string[] }>(options: T[], value: string) {
  return options.find((option) => optionMatchesValue(option, value));
}

function classNameFromClassLevel(value: string) {
  return value
    .replace(/\b(?:level|lvl)\s*\d+\b/gi, '')
    .replace(/\b\d{1,2}\b/g, '')
    .replace(/\s*-\s*$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function levelFromClassLevel(value: string) {
  const levelMatch = value.match(/\b(?:level|lvl)\s*(\d{1,2})\b/i);
  const looseMatch = value.match(/\b(\d{1,2})\b/);
  const parsed = Number(levelMatch?.[1] ?? looseMatch?.[1] ?? 1);
  if (!Number.isFinite(parsed)) return 1;
  return Math.min(20, Math.max(1, Math.round(parsed)));
}

function formatClassLevel(label: string, level: number) {
  const cleanLabel = label.replace(/\s+/g, ' ').trim();
  return cleanLabel ? `${cleanLabel} - Level ${level}` : `Level ${level}`;
}

function proficiencyBonusForLevel(level: number) {
  return 2 + Math.floor((Math.min(20, Math.max(1, level)) - 1) / 4);
}

function xpForLevel(level: number) {
  return xpThresholdsByLevel[Math.min(20, Math.max(1, level))] ?? 0;
}

function formatXpForLevel(level: number) {
  return xpForLevel(level).toLocaleString('en-US');
}

function comparableNumberText(value: string) {
  return value.replace(/,/g, '').trim();
}

function escapeRegex(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function upsertRulesSection(text: string, heading: string, body: string) {
  const cleanBody = body.trim();
  if (!cleanBody) return text;

  const current = text.trim();
  const managedHeadingPattern = managedRulesHeadings.map(escapeRegex).join('|');
  const section = `${heading}:\n${cleanBody}`;
  const pattern = new RegExp(
    `(^|\\n)${escapeRegex(heading)}:\\n[\\s\\S]*?(?=\\n(?:${managedHeadingPattern}):\\n|$)`,
    'i'
  );

  if (pattern.test(current)) {
    return current.replace(pattern, (_match, prefix: string) => `${prefix}${section}`).trim();
  }

  return [current, section].filter(Boolean).join('\n\n');
}

function applySkillProficiencies(skills: Skill[], names: string[]) {
  const skillSet = new Set(names.map((name) => name.toLowerCase()));
  return skills.map((skill) => (skillSet.has(skill.name.toLowerCase()) ? { ...skill, proficient: true } : skill));
}

function backgroundSkillNames(background: BackgroundRule) {
  const skillLine = background.proficiencies.match(/^Skills:\s*(.+)$/im)?.[1] ?? '';
  return skillLine
    .split(',')
    .map((skill) => skill.trim())
    .filter(Boolean);
}

function weaponsAreBlank(weapons: Weapon[]) {
  return weapons.every((weapon) => !weapon.name.trim() && !weapon.bonus.trim() && !weapon.damage.trim());
}

function applyClassRule(character: Character, rule: ClassRule, level: number, previousLevel = level): Character {
  const nextXp = formatXpForLevel(level);
  const previousXp = formatXpForLevel(previousLevel);
  const shouldUpdateXp =
    !character.xp.trim() || comparableNumberText(character.xp) === comparableNumberText(previousXp);

  return {
    ...character,
    classLevel: formatClassLevel(rule.label, level),
    xp: shouldUpdateXp ? nextXp : character.xp,
    proficiencyBonus: proficiencyBonusForLevel(level),
    hitDice: `${level}${rule.hitDie}`,
    savingThrowProficiencies: abilityKeys.reduce<Partial<Record<AbilityKey, boolean>>>((next, key) => {
      next[key] = rule.savingThrows.includes(key);
      return next;
    }, {}),
    proficiencies: upsertRulesSection(character.proficiencies, 'Class Proficiencies', rule.proficiencies),
    weapons:
      rule.suggestedWeapons && weaponsAreBlank(character.weapons)
        ? rule.suggestedWeapons.map((weapon) => ({ ...weapon }))
        : character.weapons,
  };
}

function applyRaceRule(character: Character, rule: RaceRule): Character {
  return {
    ...character,
    race: rule.label,
    speed: rule.speed,
    features: upsertRulesSection(character.features, 'Race Traits', rule.traits),
    proficiencies: upsertRulesSection(character.proficiencies, 'Race Languages', `Languages: ${rule.languages}`),
  };
}

function applyBackgroundRule(character: Character, rule: BackgroundRule): Character {
  return {
    ...character,
    background: rule.label,
    skills: applySkillProficiencies(character.skills, backgroundSkillNames(rule)),
    proficiencies: upsertRulesSection(character.proficiencies, 'Background Proficiencies', rule.proficiencies),
    equipment: upsertRulesSection(character.equipment, 'Background Equipment', rule.equipment),
  };
}

function buildRuleWarnings(character: Character) {
  const warnings: string[] = [];
  const level = levelFromClassLevel(character.classLevel);
  const classRule = classRuleFromClassLevel(character.classLevel);
  const raceRule = choiceRuleFromValue(raceRules, character.race);
  const cleanClassName = classNameFromClassLevel(character.classLevel);

  if (classRule) {
    const expectedProficiency = proficiencyBonusForLevel(level);
    if (character.proficiencyBonus !== expectedProficiency) {
      warnings.push(`Level ${level} characters should have a +${expectedProficiency} proficiency bonus.`);
    }

    const expectedHitDice = `${level}${classRule.hitDie}`;
    if (character.hitDice && normalizeChoice(character.hitDice) !== normalizeChoice(expectedHitDice)) {
      warnings.push(`${classRule.label} level ${level} usually has ${expectedHitDice} hit dice.`);
    }

    const hasEarlySubclass =
      level < classRule.subclassLevel &&
      classRule.subclassExamples.some((subclass) => normalizeChoice(cleanClassName).includes(normalizeChoice(subclass)));
    if (hasEarlySubclass) {
      warnings.push(`${classRule.label} subclass choices usually start at level ${classRule.subclassLevel}.`);
    }
  } else if (cleanClassName) {
    warnings.push('Custom class selected; class rules auto-fill is limited until you choose a listed class.');
  }

  if (raceRule && character.speed !== raceRule.speed) {
    warnings.push(`${raceRule.label} speed is usually ${raceRule.speed}.`);
  }

  if (character.hpMax <= 0 && level > 0) {
    warnings.push('Max hit points are still 0. Fill HP after class, level, and Constitution are decided.');
  }

  return warnings.slice(0, 4);
}

async function saveCharacterToCloudRequest(characterToSave: Character, idToken: string) {
  const response = await fetch(`${groupApiUrl}/characters/${encodeURIComponent(characterToSave.id)}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(characterToSave),
  });
  const data = (await response.json().catch(() => ({}))) as { message?: string };

  if (!response.ok) {
    throw new Error(data.message ?? 'Cloud save failed.');
  }
}

function loadCharacters(): Character[] {
  const saved = localStorage.getItem(storageKey);
  if (saved) {
    try {
      const parsed = JSON.parse(saved) as Character[];
      if (Array.isArray(parsed)) {
        return parsed.map(normalizeLoadedCharacter);
      }
    } catch {
      return [defaultCharacter];
    }
  }

  const oldSaved = localStorage.getItem(oldStorageKey);
  if (oldSaved) {
    try {
      return [normalizeLoadedCharacter({ ...JSON.parse(oldSaved), id: 'migrated-character' })];
    } catch {
      return [defaultCharacter];
    }
  }

  return [defaultCharacter];
}

function createChatSession(): ChatSession {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    title: 'New Chat',
    messages: [],
    createdAt: now,
    updatedAt: now,
  };
}

function cleanBuilderChoice(choice: Partial<BuilderChoice>, index: number): BuilderChoice | null {
  const label = String(choice.label ?? '').replace(/\s+/g, ' ').trim().slice(0, 80);
  const question = String(choice.question ?? '').replace(/\s+/g, ' ').trim().slice(0, 220);
  const options = Array.isArray(choice.options)
    ? choice.options
        .map((option) => String(option).replace(/\s+/g, ' ').trim().slice(0, 80))
        .filter(Boolean)
        .slice(0, 8)
    : [];

  if (!label || !question || options.length < 2) return null;

  return {
    id:
      String(choice.id ?? label)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 44) || `choice-${index + 1}`,
    label,
    question,
    recommendation: String(choice.recommendation ?? '').replace(/\s+/g, ' ').trim().slice(0, 140) || undefined,
    options,
    allowCustom: choice.allowCustom !== false,
    customLabel: String(choice.customLabel ?? '').replace(/\s+/g, ' ').trim().slice(0, 80) || 'Other',
  };
}

function cleanBuilderChoices(choices?: Partial<BuilderChoice>[]) {
  if (!Array.isArray(choices)) return undefined;
  const cleaned = choices
    .map(cleanBuilderChoice)
    .filter((choice): choice is BuilderChoice => Boolean(choice))
    .slice(0, 6);
  return cleaned.length ? cleaned : undefined;
}

function stripVisibleSheetPatchText(content: string) {
  return content
    .replace(/<sheet_patch>[\s\S]*?<\/sheet_patch>/gi, '')
    .replace(/(^|\n)\s*#{1,6}\s*sheet\s*patch\b[\s\S]*?(?=\n\s*#{1,6}\s+\S|$)/gi, '$1')
    .replace(/(^|\n)\s*sheet\s*patch\s*:?\s*\n[\s\S]*?(?=\n\s*#{1,6}\s+\S|$)/gi, '$1')
    .replace(
      /(^|\n)\s*here(?:'s| is) a compact json object to update your character sheet:?\s*```(?:json)?[\s\S]*?```/gi,
      '$1'
    )
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function renderInlineChatMarkdown(text: string, keyPrefix: string) {
  const parts: ReactNode[] = [];
  const boldPattern = /\*\*([^*\n]+)\*\*/g;
  let lastIndex = 0;
  let index = 0;

  for (const match of text.matchAll(boldPattern)) {
    const start = match.index ?? 0;
    if (start > lastIndex) parts.push(text.slice(lastIndex, start));
    parts.push(<strong key={`${keyPrefix}-bold-${index}`}>{match[1]}</strong>);
    lastIndex = start + match[0].length;
    index += 1;
  }

  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length ? parts : text;
}

function stripChatHeadingMarker(line: string) {
  return line.replace(/^\s*#{1,6}\s+/, '').trimEnd();
}

function ChatMarkdown({ content }: { content: string }) {
  const blocks = content
    .replace(/\r\n/g, '\n')
    .trim()
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);

  if (!blocks.length) return null;

  return (
    <div className="chat-markdown">
      {blocks.map((block, blockIndex) => {
        const lines = block.split('\n').map((line) => line.trimEnd()).filter(Boolean);
        const orderedItems = lines.map((line) => line.match(/^\s*\d+[.)]\s+(.+)$/)?.[1]);
        const bulletItems = lines.map((line) => line.match(/^\s*[-*]\s+(.+)$/)?.[1]);

        if (orderedItems.every(Boolean)) {
          return (
            <ol key={`chat-block-${blockIndex}`}>
              {orderedItems.map((item, itemIndex) => (
                <li key={`chat-block-${blockIndex}-item-${itemIndex}`}>
                  {renderInlineChatMarkdown(item ?? '', `chat-block-${blockIndex}-item-${itemIndex}`)}
                </li>
              ))}
            </ol>
          );
        }

        if (bulletItems.every(Boolean)) {
          return (
            <ul key={`chat-block-${blockIndex}`}>
              {bulletItems.map((item, itemIndex) => (
                <li key={`chat-block-${blockIndex}-item-${itemIndex}`}>
                  {renderInlineChatMarkdown(item ?? '', `chat-block-${blockIndex}-item-${itemIndex}`)}
                </li>
              ))}
            </ul>
          );
        }

        return (
          <p key={`chat-block-${blockIndex}`}>
            {lines.map((line, lineIndex) => {
              const isHeading = /^\s*#{1,6}\s+/.test(line);
              const text = stripChatHeadingMarker(line);
              return (
                <span className={isHeading ? 'chat-markdown-heading' : undefined} key={`chat-line-${blockIndex}-${lineIndex}`}>
                  {lineIndex > 0 ? <br /> : null}
                  {renderInlineChatMarkdown(text, `chat-line-${blockIndex}-${lineIndex}`)}
                </span>
              );
            })}
          </p>
        );
      })}
    </div>
  );
}

function cleanChatMessage(message: Partial<ChatMessage>): ChatMessage | null {
  const role = message.role === 'assistant' ? 'assistant' : message.role === 'user' ? 'user' : null;
  const content = stripVisibleSheetPatchText(String(message.content ?? '').slice(0, 5000));
  const builderChoices = cleanBuilderChoices(message.builderChoices);
  if (!role || (!content.trim() && !message.patch && !builderChoices?.length)) return null;

  return {
    role,
    content: content || (message.patch ? 'I drafted an update for your sheet.' : 'Choose the next options below.'),
    sources: Array.isArray(message.sources)
      ? message.sources.map((source) => String(source).slice(0, 160)).filter(Boolean).slice(0, 8)
      : undefined,
    patch: message.patch ?? null,
    builderChoices,
  };
}

function chatTitleFromMessages(messages: ChatMessage[], fallback = 'New Chat') {
  const firstUserMessage = messages.find((message) => message.role === 'user')?.content.replace(/\s+/g, ' ').trim();
  if (!firstUserMessage) return fallback || 'New Chat';
  return firstUserMessage.length > 48 ? `${firstUserMessage.slice(0, 45)}...` : firstUserMessage;
}

function normalizeChatSession(session: Partial<ChatSession>): ChatSession {
  const now = new Date().toISOString();
  const messages = Array.isArray(session.messages)
    ? session.messages.map(cleanChatMessage).filter((message): message is ChatMessage => Boolean(message)).slice(-maxChatMessages)
    : [];

  return {
    id: session.id || crypto.randomUUID(),
    title: chatTitleFromMessages(messages, session.title || 'New Chat'),
    messages,
    createdAt: session.createdAt || now,
    updatedAt: session.updatedAt || session.createdAt || now,
  };
}

function loadChatSessions(): ChatSession[] {
  const saved = localStorage.getItem(chatHistoryStorageKey);
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        const sessions = parsed
          .map(normalizeChatSession)
          .sort((a, b) => timestamp(b.updatedAt) - timestamp(a.updatedAt))
          .slice(0, maxChatSessions);
        if (sessions.length) return sessions;
      }
    } catch {
      return [createChatSession()];
    }
  }

  return [createChatSession()];
}

function saveableChatSessions(sessions: ChatSession[]) {
  return sessions
    .map((session) => ({
      ...session,
      title: chatTitleFromMessages(session.messages, session.title),
      messages: session.messages
        .map(cleanChatMessage)
        .filter((message): message is ChatMessage => Boolean(message))
        .slice(-maxChatMessages),
    }))
    .sort((a, b) => timestamp(b.updatedAt) - timestamp(a.updatedAt))
    .slice(0, maxChatSessions);
}

function titleCaseSheetItem(value: string) {
  const keepLower = new Set(['a', 'an', 'and', 'as', 'at', 'by', 'for', 'from', 'in', 'of', 'on', 'or', 'per', 'the', 'to', 'with']);
  const keepUpper = new Set(['ac', 'aoe', 'dc', 'dm', 'hp', 'npc', 'xp']);

  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (keepUpper.has(lower)) return lower.toUpperCase();
      if (index > 0 && keepLower.has(lower)) return lower;
      return lower ? `${lower[0].toUpperCase()}${lower.slice(1)}` : lower;
    })
    .join(' ');
}

function prettySheetListItem(value: string) {
  const normalized = value.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  if (normalized === normalized.toLowerCase() || /[_-]/.test(value)) return titleCaseSheetItem(normalized);
  return normalized;
}

function prettySheetListText(value?: string) {
  const text = String(value ?? '')
    .replace(/\u0000/g, '')
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .trim();
  if (!text) return '';

  return text
    .split('\n')
    .flatMap((line) => {
      const trimmed = line.trim();
      if (!trimmed) return [];

      const parts = trimmed
        .split(/\s*,\s*/)
        .map((part) => part.trim())
        .filter(Boolean);
      const shouldSplit =
        parts.length > 1 &&
        !/[.!?;]/.test(trimmed) &&
        !parts.some((part) => /^(and|or)\b/i.test(part)) &&
        (trimmed.includes('_') || /,\S/.test(trimmed) || parts.every((part) => part.length <= 42));

      return shouldSplit ? parts.map(prettySheetListItem) : [prettySheetListItem(trimmed)];
    })
    .filter(Boolean)
    .join('\n');
}

function repairLoadedListText(value?: string) {
  const text = String(value ?? '');
  if (!text.includes('_') && !/,\S/.test(text)) return text;
  return prettySheetListText(text);
}

function prettifyCharacterPatch(patch: CharacterPatch): CharacterPatch {
  const cleanPatch: CharacterPatch = { ...patch };
  if (patch.equipment !== undefined) cleanPatch.equipment = prettySheetListText(patch.equipment);
  if (patch.features !== undefined) cleanPatch.features = prettySheetListText(patch.features);
  if (patch.proficiencies !== undefined) cleanPatch.proficiencies = prettySheetListText(patch.proficiencies);
  return cleanPatch;
}

function normalizeLoadedCharacter(character: Partial<Character>): Character {
  const normalized = {
    ...defaultCharacter,
    ...character,
    id: character.id || crypto.randomUUID(),
    abilities: character.abilities?.length ? character.abilities : blankAbilities.map((ability) => ({ ...ability })),
    skills: character.skills?.length ? character.skills : blankSkills.map((skill) => ({ ...skill })),
    weapons: character.weapons?.length ? character.weapons : blankWeapons.map((weapon) => ({ ...weapon })),
    attackNotes: character.attackNotes ?? '',
    savingThrowProficiencies: character.savingThrowProficiencies ?? {},
    savingThrowOverrides: character.savingThrowOverrides ?? {},
    skillOverrides: character.skillOverrides ?? {},
    personalNotes: character.personalNotes ?? {},
    updatedAt: character.updatedAt || new Date().toISOString(),
  };

  return {
    ...normalized,
    equipment: repairLoadedListText(normalized.equipment),
    features: repairLoadedListText(normalized.features),
    proficiencies: repairLoadedListText(normalized.proficiencies),
  };
}

function timestamp(value?: string) {
  const parsed = Date.parse(value ?? '');
  return Number.isFinite(parsed) ? parsed : 0;
}

function mergeCharacterLists(localCharacters: Character[], cloudCharacters: Character[]) {
  const merged = new Map<string, Character>();

  for (const character of localCharacters.map(normalizeLoadedCharacter)) {
    merged.set(character.id, character);
  }

  for (const character of cloudCharacters.map(normalizeLoadedCharacter)) {
    const current = merged.get(character.id);
    if (!current || timestamp(character.updatedAt) >= timestamp(current.updatedAt)) {
      merged.set(character.id, character);
    }
  }

  return Array.from(merged.values()).sort((a, b) => timestamp(b.updatedAt) - timestamp(a.updatedAt));
}

function loadDmNotes(): Record<string, string> {
  const saved = localStorage.getItem(dmNotesStorageKey);
  if (!saved) return {};

  try {
    const parsed = JSON.parse(saved) as Record<string, string>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function clampHitPoint(value: number, fallback = 0) {
  if (!Number.isFinite(value)) return fallback;
  return Math.max(0, Math.min(9999, Math.round(value)));
}

function modifier(score: number) {
  return Math.floor((score - 10) / 2);
}

function formatMod(value: number) {
  return value >= 0 ? `+${value}` : String(value);
}

function characterSummary(character: Character): GroupMember {
  return {
    id: character.id,
    name: character.name,
    player: character.player,
    classLevel: character.classLevel,
    race: character.race,
    alignment: character.alignment,
    armorClass: character.armorClass,
    hpCurrent: character.hpCurrent,
    hpMax: character.hpMax,
    portraitDataUrl: character.portraitDataUrl,
    updatedAt: character.updatedAt,
  };
}

function characterChatContext(character: Character) {
  return {
    name: character.name,
    player: character.player,
    classLevel: character.classLevel,
    background: character.background,
    race: character.race,
    alignment: character.alignment,
    armorClass: character.armorClass,
    initiative: character.initiative,
    speed: character.speed,
    hpCurrent: character.hpCurrent,
    hpMax: character.hpMax,
    hpTemp: character.hpTemp,
    proficiencyBonus: character.proficiencyBonus,
    passivePerception: character.passivePerception,
    abilities: character.abilities,
    skills: character.skills,
    weapons: character.weapons,
    attackNotes: character.attackNotes,
    features: character.features,
    proficiencies: character.proficiencies,
    equipment: character.equipment,
    traits: character.traits,
    ideals: character.ideals,
    bonds: character.bonds,
    flaws: character.flaws,
  };
}

function formatMicroUsd(value?: number) {
  const dollars = Math.max(0, Number(value ?? 0)) / 1_000_000;
  if (dollars > 0 && dollars < 0.01) return `$${dollars.toFixed(4)}`;
  return `$${dollars.toFixed(2)}`;
}

async function resizeImage(file: File): Promise<string> {
  const imageUrl = URL.createObjectURL(file);
  const image = new Image();
  image.src = imageUrl;
  await image.decode();

  const canvas = document.createElement('canvas');
  const maxSide = 560;
  const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
  canvas.width = Math.round(image.width * scale);
  canvas.height = Math.round(image.height * scale);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not prepare image.');
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  URL.revokeObjectURL(imageUrl);
  return canvas.toDataURL('image/jpeg', 0.78);
}

async function resizeSheetImage(file: File): Promise<string> {
  const imageUrl = URL.createObjectURL(file);
  const image = new Image();
  image.src = imageUrl;
  await image.decode();

  const canvas = document.createElement('canvas');
  const maxSide = 1800;
  const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
  canvas.width = Math.round(image.width * scale);
  canvas.height = Math.round(image.height * scale);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Could not prepare sheet image.');
  context.fillStyle = '#fffaf0';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  URL.revokeObjectURL(imageUrl);
  return canvas.toDataURL('image/jpeg', 0.84);
}

function parseReference(markdown: string): Record<string, ReferenceEntry> {
  const entries: Record<string, ReferenceEntry> = {};
  const sections = markdown.split(/^##\s+/m).slice(1);

  for (const section of sections) {
    const [heading, ...bodyLines] = section.trim().split(/\r?\n/);
    const title = heading.trim();
    const key = title.toLowerCase();
    const body = bodyLines.join('\n').replace(/\s+/g, ' ').trim();
    if (title && body) entries[key] = { key, title, body };
  }

  return entries;
}

function App() {
  const [initialChatState] = useState(() => {
    const sessions = loadChatSessions();
    return { sessions, activeId: sessions[0]?.id ?? '' };
  });
  const [authSession, setAuthSession] = useState<AuthSession | null>(() =>
    authConfig.enabled ? getStoredSession() : null
  );
  const [authReady, setAuthReady] = useState(!authConfig.enabled);
  const [authMode, setAuthMode] = useState<AuthMode>('signIn');
  const [authError, setAuthError] = useState('');
  const [authNotice, setAuthNotice] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authNewPassword, setAuthNewPassword] = useState('');
  const [authResetCode, setAuthResetCode] = useState('');
  const [authChallenge, setAuthChallenge] = useState<NewPasswordChallenge | null>(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [characters, setCharacters] = useState<Character[]>(loadCharacters);
  const [activeCharacterId, setActiveCharacterId] = useState(characters[0]?.id ?? defaultCharacter.id);
  const [viewMode, setViewMode] = useState<ViewMode>('library');
  const [reference, setReference] = useState<Record<string, ReferenceEntry>>({});
  const [activeTab, setActiveTab] = useState<SheetTab>('stats');
  const [search, setSearch] = useState('');
  const [selectedHelp, setSelectedHelp] = useState<ReferenceEntry | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Character | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [groupCode, setGroupCode] = useState(localStorage.getItem('dnd-character-org-group-code') ?? '');
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [groupStatus, setGroupStatus] = useState('');
  const [chatSessions, setChatSessions] = useState<ChatSession[]>(initialChatState.sessions);
  const [activeChatId, setActiveChatId] = useState(initialChatState.activeId);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [chatError, setChatError] = useState('');
  const [chatBudget, setChatBudget] = useState<ChatBudget | null>(null);
  const [cloudReady, setCloudReady] = useState(false);
  const [cloudStatus, setCloudStatus] = useState('Saved locally');
  const [cloudSaveBusy, setCloudSaveBusy] = useState(false);
  const [pendingCloudSaveId, setPendingCloudSaveId] = useState<string | null>(null);
  const [dmNotesByGroup, setDmNotesByGroup] = useState<Record<string, string>>(loadDmNotes);

  const character = characters.find((entry) => entry.id === activeCharacterId) ?? characters[0] ?? defaultCharacter;
  const normalizedGroupCode = groupCode.trim().toUpperCase();
  const dmNotesKey = normalizedGroupCode || 'ungrouped';
  const dmNotes = dmNotesByGroup[dmNotesKey] ?? '';
  const activeChat = chatSessions.find((session) => session.id === activeChatId) ?? chatSessions[0];
  const chatMessages = activeChat?.messages ?? [];

  useEffect(() => {
    if (!authConfig.enabled) return;

    setAuthSession(getStoredSession());
    setAuthReady(true);
  }, []);

  useEffect(() => {
    fetch('/dnd-reference.md')
      .then((response) => response.text())
      .then((text) => setReference(parseReference(text)))
      .catch(() => setReference({}));
  }, []);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(characters));
  }, [characters]);

  useEffect(() => {
    const normalized = groupCode.trim().toUpperCase();
    if (normalized) {
      localStorage.setItem('dnd-character-org-group-code', normalized);
    } else {
      localStorage.removeItem('dnd-character-org-group-code');
    }
  }, [groupCode]);

  useEffect(() => {
    localStorage.setItem(dmNotesStorageKey, JSON.stringify(dmNotesByGroup));
  }, [dmNotesByGroup]);

  useEffect(() => {
    localStorage.setItem(chatHistoryStorageKey, JSON.stringify(saveableChatSessions(chatSessions)));
  }, [chatSessions]);

  useEffect(() => {
    if (!chatSessions.length) {
      const nextSession = createChatSession();
      setChatSessions([nextSession]);
      setActiveChatId(nextSession.id);
      return;
    }

    if (!chatSessions.some((session) => session.id === activeChatId)) {
      setActiveChatId(chatSessions[0].id);
    }
  }, [activeChatId, chatSessions]);

  useEffect(() => {
    if (!authSession || !groupApiUrl) return;

    fetch(`${groupApiUrl}/ai/usage`, {
      headers: { Authorization: `Bearer ${authSession.idToken}` },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (data?.budget) setChatBudget(data.budget);
      })
      .catch(() => undefined);
  }, [authSession]);

  useEffect(() => {
    if (!authSession || !groupApiUrl) {
      setCloudReady(false);
      setCloudStatus('Saved locally');
      return;
    }

    let cancelled = false;
    setCloudReady(false);
    setCloudStatus('Loading cloud saves...');

    fetch(`${groupApiUrl}/characters`, {
      headers: { Authorization: `Bearer ${authSession.idToken}` },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cloud saves could not be loaded.');
        return response.json();
      })
      .then((data: { characters?: Partial<Character>[] }) => {
        if (cancelled) return;

        let mergedSnapshot: Character[] = [];
        const cloudCharacters = (data.characters ?? []).map(normalizeLoadedCharacter);
        setCharacters((current) => {
          mergedSnapshot = mergeCharacterLists(current, cloudCharacters);
          return mergedSnapshot.length ? mergedSnapshot : current;
        });
        setActiveCharacterId((currentId) =>
          mergedSnapshot.some((entry) => entry.id === currentId)
            ? currentId
            : mergedSnapshot[0]?.id ?? currentId
        );
        setCloudStatus(cloudCharacters.length ? 'Cloud saves loaded' : 'Cloud ready');
        setCloudReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setCloudStatus('Saved locally; cloud load failed');
        setCloudReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [authSession]);

  useEffect(() => {
    if (!authSession || !groupApiUrl || !cloudReady || !pendingCloudSaveId) return;

    const characterToSave = characters.find((entry) => entry.id === pendingCloudSaveId);
    if (!characterToSave) return;

    setCloudStatus('Cloud autosave queued');
    const timeout = window.setTimeout(async () => {
      setCloudStatus('Saving to cloud...');
      try {
        await saveCharacterToCloudRequest(characterToSave, authSession.idToken);
        setPendingCloudSaveId((current) => (current === characterToSave.id ? null : current));
        setCloudStatus('Cloud saved');
      } catch {
        setCloudStatus('Saved locally; cloud save failed');
      }
    }, 6000);

    return () => window.clearTimeout(timeout);
  }, [authSession, characters, cloudReady, pendingCloudSaveId]);

  const abilityMods = useMemo(() => {
    return character.abilities.reduce<Record<AbilityKey, number>>((mods, ability) => {
      mods[ability.key] = modifier(ability.score);
      return mods;
    }, {} as Record<AbilityKey, number>);
  }, [character.abilities]);

  const filteredReference = Object.values(reference).filter((entry) => {
    const query = search.toLowerCase();
    return entry.title.toLowerCase().includes(query) || entry.body.toLowerCase().includes(query);
  });
  const ruleWarnings = useMemo(() => buildRuleWarnings(character), [character]);

  const updateCharacter = (nextCharacter: Character) => {
    setCharacters((current) =>
      current.map((entry) =>
        entry.id === nextCharacter.id ? { ...nextCharacter, updatedAt: new Date().toISOString() } : entry
      )
    );
    if (authSession && cloudReady) {
      setCloudStatus('Unsaved cloud changes');
    }
    setPendingCloudSaveId(nextCharacter.id);
  };

  const update = <Key extends keyof Character>(key: Key, value: Character[Key]) => {
    updateCharacter({ ...character, [key]: value });
  };

  const saveActiveCharacterToCloud = async (reason: 'manual' | 'ai' = 'manual', characterToSave = character) => {
    if (!authSession || !groupApiUrl) {
      setCloudStatus('Saved locally');
      return false;
    }

    if (!cloudReady) {
      setCloudStatus('Cloud is still loading');
      return false;
    }

    setCloudSaveBusy(true);
    setCloudStatus(reason === 'ai' ? 'Saving sheet before AI...' : 'Saving to cloud...');
    try {
      await saveCharacterToCloudRequest(characterToSave, authSession.idToken);
      setPendingCloudSaveId((current) => (current === characterToSave.id ? null : current));
      setCloudStatus(reason === 'ai' ? 'Cloud saved; asking AI...' : 'Cloud saved');
      return true;
    } catch {
      setCloudStatus('Saved locally; cloud save failed');
      return false;
    } finally {
      setCloudSaveBusy(false);
    }
  };

  const applyClassSelection = (classId: string) => {
    const rule = classRules.find((entry) => entry.id === classId);
    if (!rule) return;

    updateCharacter(applyClassRule(character, rule, levelFromClassLevel(character.classLevel)));
  };

  const applyLevelSelection = (level: number) => {
    const currentLevel = levelFromClassLevel(character.classLevel);
    const rule = classRuleFromClassLevel(character.classLevel);

    if (rule) {
      updateCharacter(applyClassRule(character, rule, level, currentLevel));
      return;
    }

    const currentXp = formatXpForLevel(currentLevel);
    const shouldUpdateXp =
      !character.xp.trim() || comparableNumberText(character.xp) === comparableNumberText(currentXp);
    updateCharacter({
      ...character,
      classLevel: formatClassLevel(classNameFromClassLevel(character.classLevel), level),
      xp: shouldUpdateXp ? formatXpForLevel(level) : character.xp,
      proficiencyBonus: proficiencyBonusForLevel(level),
    });
  };

  const applyManualClassName = (className: string) => {
    const level = levelFromClassLevel(character.classLevel);
    updateCharacter({
      ...character,
      classLevel: formatClassLevel(className, level),
      proficiencyBonus: proficiencyBonusForLevel(level),
    });
  };

  const applyRaceSelection = (raceId: string) => {
    const rule = raceRules.find((entry) => entry.id === raceId);
    if (rule) updateCharacter(applyRaceRule(character, rule));
  };

  const applyBackgroundSelection = (backgroundId: string) => {
    const rule = backgroundRules.find((entry) => entry.id === backgroundId);
    if (rule) updateCharacter(applyBackgroundRule(character, rule));
  };

  const applyAlignmentSelection = (alignmentId: string) => {
    const rule = alignmentRules.find((entry) => entry.id === alignmentId);
    if (rule) update('alignment', rule.label);
  };

  const updateAbility = (key: AbilityKey, score: number) => {
    updateCharacter({
      ...character,
      abilities: character.abilities.map((ability) =>
        ability.key === key ? { ...ability, score } : ability
      ),
    });
  };

  const updateSkill = (name: string, patch: Partial<Skill>) => {
    updateCharacter({
      ...character,
      skills: character.skills.map((skill) => (skill.name === name ? { ...skill, ...patch } : skill)),
    });
  };

  const updateSavingThrowProficiency = (key: AbilityKey, proficient: boolean) => {
    update('savingThrowProficiencies', {
      ...(character.savingThrowProficiencies ?? {}),
      [key]: proficient,
    });
  };

  const updateSavingThrowOverride = (key: AbilityKey, value: string) => {
    update('savingThrowOverrides', {
      ...(character.savingThrowOverrides ?? {}),
      [key]: value,
    });
  };

  const updateSkillOverride = (name: string, value: string) => {
    update('skillOverrides', {
      ...(character.skillOverrides ?? {}),
      [name]: value,
    });
  };

  const updateChatMessages = (chatId: string, updater: (messages: ChatMessage[]) => ChatMessage[]) => {
    if (!chatId) return;

    setChatSessions((current) =>
      current
        .map((session) => {
          if (session.id !== chatId) return session;

          const now = new Date().toISOString();
          const messages = updater(session.messages).slice(-maxChatMessages);
          return {
            ...session,
            messages,
            title: chatTitleFromMessages(messages, session.title),
            updatedAt: now,
          };
        })
        .sort((a, b) => timestamp(b.updatedAt) - timestamp(a.updatedAt))
    );
  };

  const updateActiveChatMessages = (updater: (messages: ChatMessage[]) => ChatMessage[]) => {
    updateChatMessages(activeChatId, updater);
  };

  const applyCharacterPatch = (patch?: CharacterPatch | null) => {
    if (!patch) return;

    const cleanPatch = prettifyCharacterPatch(patch);
    const abilityUpdates = new Map((cleanPatch.abilities ?? []).map((ability) => [ability.key, ability]));
    const skillUpdates = new Map((cleanPatch.skills ?? []).map((skill) => [skill.name.toLowerCase(), skill]));
    const nextCharacter: Character = {
      ...character,
      ...cleanPatch,
      id: character.id,
      updatedAt: character.updatedAt,
      portraitDataUrl: character.portraitDataUrl,
      abilities: cleanPatch.abilities?.length
        ? character.abilities.map((ability) => ({ ...ability, ...(abilityUpdates.get(ability.key) ?? {}) }))
        : character.abilities,
      skills: cleanPatch.skills?.length
        ? character.skills.map((skill) => ({ ...skill, ...(skillUpdates.get(skill.name.toLowerCase()) ?? {}) }))
        : character.skills,
      weapons: cleanPatch.weapons?.length ? cleanPatch.weapons : character.weapons,
      savingThrowProficiencies: {
        ...(character.savingThrowProficiencies ?? {}),
        ...(cleanPatch.savingThrowProficiencies ?? {}),
      },
      savingThrowOverrides: {
        ...(character.savingThrowOverrides ?? {}),
        ...(cleanPatch.savingThrowOverrides ?? {}),
      },
      skillOverrides: {
        ...(character.skillOverrides ?? {}),
        ...(cleanPatch.skillOverrides ?? {}),
      },
      personalNotes: {
        ...(character.personalNotes ?? {}),
        ...(cleanPatch.personalNotes ?? {}),
      },
    };

    updateCharacter(nextCharacter);
    updateActiveChatMessages((current) => [
      ...current,
      {
        role: 'assistant',
        content: `${nextCharacter.name || 'Character'} was updated on the sheet. Cloud autosave will run in a few seconds.`,
      },
    ]);
  };

  const startNewChat = () => {
    const nextSession = createChatSession();
    setChatSessions((current) => [nextSession, ...current].slice(0, maxChatSessions));
    setActiveChatId(nextSession.id);
    setChatInput('');
    setChatError('');
  };

  const deleteChat = () => {
    const remaining = chatSessions.filter((session) => session.id !== activeChatId);
    const nextSessions = remaining.length ? remaining : [createChatSession()];
    setChatSessions(nextSessions);
    setActiveChatId(nextSessions[0].id);
    setChatInput('');
    setChatError('');
  };

  const selectChat = (id: string) => {
    setActiveChatId(id);
    setChatError('');
  };

  const updateWeapon = (index: number, patch: Partial<Weapon>) => {
    updateCharacter({
      ...character,
      weapons: character.weapons.map((weapon, weaponIndex) =>
        weaponIndex === index ? { ...weapon, ...patch } : weapon
      ),
    });
  };

  const helpFor = (key: string) => reference[key.toLowerCase()];
  const proficiencyBonusHelp = helpFor('Proficiency Bonus');
  const openHelp = (key: string, fallbackTitle?: string) => {
    const normalized = key.toLowerCase();
    setSelectedHelp(reference[normalized] ?? { key: normalized, title: fallbackTitle ?? key, body: 'Add personal notes for this section.' });
  };

  const exportSheet = () => {
    const blob = new Blob([JSON.stringify(character, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${character.name || 'character'}-sheet.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const selectCharacter = (id: string) => {
    setActiveCharacterId(id);
    setViewMode('sheet');
    setActiveTab('stats');
  };

  const openDmView = () => {
    setViewMode('dm');
    if (normalizedGroupCode) void loadGroup(normalizedGroupCode);
  };

  const addCharacter = () => {
    const nextCharacter = createBlankCharacter();
    setCharacters((current) => [nextCharacter, ...current]);
    setActiveCharacterId(nextCharacter.id);
    setPendingCloudSaveId(nextCharacter.id);
    setViewMode('sheet');
    setActiveTab('stats');
  };

  const requestDeleteCharacter = () => {
    setDeleteTarget(character);
    setDeleteConfirmName('');
  };

  const cancelDeleteCharacter = () => {
    setDeleteTarget(null);
    setDeleteConfirmName('');
  };

  const confirmDeleteCharacter = () => {
    if (!deleteTarget) return;

    const expectedName = deleteTarget.name.trim() || 'Unnamed Adventurer';
    if (deleteConfirmName.trim() !== expectedName) return;

    const remaining = characters.filter((entry) => entry.id !== deleteTarget.id);
    setCharacters(remaining);
    setActiveCharacterId(remaining[0]?.id ?? '');
    setViewMode('library');
    setActiveTab('stats');
    setPendingCloudSaveId((current) => (current === deleteTarget.id ? null : current));
    if (authSession) void deleteCloudCharacter(deleteTarget.id);
    cancelDeleteCharacter();
  };

  const deleteCloudCharacter = async (id: string) => {
    if (!authSession || !groupApiUrl) return;

    try {
      await fetch(`${groupApiUrl}/characters/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authSession.idToken}` },
      });
      setCloudStatus('Deleted from cloud');
    } catch {
      setCloudStatus('Deleted locally; cloud delete failed');
    }
  };

  const updateDmNotes = (value: string) => {
    setDmNotesByGroup((current) => ({
      ...current,
      [dmNotesKey]: value,
    }));
  };

  const loadGroup = async (code = groupCode) => {
    const normalized = code.trim().toUpperCase();
    if (!groupApiUrl || !normalized) return;

    setGroupStatus('Loading group...');
    try {
      const response = await fetch(`${groupApiUrl}/groups/${encodeURIComponent(normalized)}`, {
        headers: authSession ? { Authorization: `Bearer ${authSession.idToken}` } : undefined,
      });
      if (response.status === 401) {
        setGroupStatus('Sign in again before using groups.');
        return;
      }
      if (!response.ok) throw new Error('Group could not be loaded.');
      const data = (await response.json()) as { characters: GroupMember[] };
      setGroupMembers(data.characters ?? []);
      setGroupStatus(`${normalized} loaded`);
    } catch {
      setGroupStatus('Group sync is unavailable right now.');
    }
  };

  const joinGroup = async () => {
    const normalized = groupCode.trim().toUpperCase();
    if (!normalized) {
      setGroupStatus('Enter a group code first.');
      return;
    }
    setGroupCode(normalized);
    await loadGroup(normalized);
  };

  const clearGroup = () => {
    setGroupCode('');
    setGroupMembers([]);
    setGroupStatus('');
    localStorage.removeItem('dnd-character-org-group-code');
  };

  const sendAiGuideQuestion = async (rawQuestion: string) => {
    const question = rawQuestion.trim();
    if (!question || chatBusy) return;

    if (!authSession) {
      setChatError('Sign in before asking the AI guide.');
      return;
    }

    const userMessage: ChatMessage = { role: 'user', content: question };
    const requestChatId = activeChatId;
    const history = chatMessages.slice(-6);
    const characterForQuestion = character;
    updateChatMessages(requestChatId, (current) => [...current, userMessage]);
    setChatInput('');
    setChatBusy(true);
    setChatError('');

    try {
      await saveActiveCharacterToCloud('ai', characterForQuestion);
      const response = await fetch(`${groupApiUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authSession.idToken}`,
        },
        body: JSON.stringify({
          question,
          history,
          character: characters.length > 0 ? characterChatContext(characterForQuestion) : undefined,
          groupCode: normalizedGroupCode || undefined,
          allowSheetPatch: true,
        }),
      });
      const data = (await response.json().catch(() => ({}))) as {
        answer?: string;
        message?: string;
        sources?: string[];
        patch?: CharacterPatch | null;
        builderChoices?: BuilderChoice[];
        budget?: ChatBudget;
      };

      if (response.status === 401) throw new Error('Sign in again before using the AI guide.');
      if (response.status === 402) throw new Error(data.message ?? 'The $20 AI usage limit has been reached.');
      if (!response.ok) throw new Error(data.message ?? 'The AI guide could not answer right now.');

      updateChatMessages(requestChatId, (current) => [
        ...current,
        {
          role: 'assistant',
          content: stripVisibleSheetPatchText(data.answer ?? 'I could not produce an answer for that question.'),
          sources: data.sources ?? [],
          patch: data.patch,
          builderChoices: cleanBuilderChoices(data.builderChoices),
        },
      ]);
      if (data.budget) setChatBudget(data.budget);
    } catch (error) {
      setChatError(error instanceof Error ? error.message : 'The AI guide could not answer right now.');
    } finally {
      setChatBusy(false);
    }
  };

  const askAiGuide = async () => {
    await sendAiGuideQuestion(chatInput);
  };

  const importSheetPhoto = async (file: File | null) => {
    if (!file) return;

    if (!authSession) {
      setChatError('Sign in before importing a sheet photo.');
      return;
    }

    setChatBusy(true);
    setChatError('');
    const requestChatId = activeChatId;
    updateChatMessages(requestChatId, (current) => [
      ...current,
      { role: 'user', content: `Uploaded ${file.name} to build a character from a hard-copy sheet.` },
    ]);

    try {
      const imageDataUrl = await resizeSheetImage(file);
      const response = await fetch(`${groupApiUrl}/sheet-photo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authSession.idToken}`,
        },
        body: JSON.stringify({
          imageDataUrl,
          question: 'Create or update this character from the uploaded hard-copy sheet.',
          character: characters.length > 0 ? characterChatContext(character) : undefined,
        }),
      });
      const data = (await response.json().catch(() => ({}))) as {
        answer?: string;
        message?: string;
        sources?: string[];
        patch?: CharacterPatch | null;
        budget?: ChatBudget;
      };

      if (response.status === 401) throw new Error('Sign in again before importing a sheet photo.');
      if (response.status === 402) throw new Error(data.message ?? 'The $20 AI usage limit has been reached.');
      if (!response.ok) throw new Error(data.message ?? 'The sheet photo could not be read right now.');

      updateChatMessages(requestChatId, (current) => [
        ...current,
        {
          role: 'assistant',
          content: stripVisibleSheetPatchText(data.answer ?? 'I read the sheet photo and prepared an update.'),
          sources: data.sources ?? [],
          patch: data.patch,
        },
      ]);
      if (data.budget) setChatBudget(data.budget);
    } catch (error) {
      setChatError(error instanceof Error ? error.message : 'The sheet photo could not be read right now.');
    } finally {
      setChatBusy(false);
    }
  };

  const shareToGroup = async () => {
    const normalized = groupCode.trim().toUpperCase();
    if (!groupApiUrl || !normalized) {
      setGroupStatus('Join a group before sharing.');
      return;
    }

    setGroupStatus('Sharing character...');
    try {
      const response = await fetch(`${groupApiUrl}/groups/${encodeURIComponent(normalized)}/characters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authSession ? { Authorization: `Bearer ${authSession.idToken}` } : {}),
        },
        body: JSON.stringify(characterSummary(character)),
      });
      if (response.status === 401) {
        setGroupStatus('Sign in again before sharing.');
        return;
      }
      if (!response.ok) throw new Error('Share failed.');
      await loadGroup(normalized);
      setGroupStatus(`${character.name} shared to ${normalized}`);
    } catch {
      setGroupStatus('Could not share to the group.');
    }
  };

  const updateGroupMemberHp = async (
    member: GroupMember,
    patch: Partial<Pick<GroupMember, 'hpCurrent' | 'hpMax'>>
  ) => {
    const groupMemberId = member.groupMemberId;
    if (!groupApiUrl || !normalizedGroupCode || !groupMemberId) {
      setGroupStatus('Refresh the group before tracking HP.');
      return;
    }

    const nextMax = Math.max(1, clampHitPoint(patch.hpMax ?? member.hpMax, member.hpMax || 1));
    const nextCurrent = Math.min(clampHitPoint(patch.hpCurrent ?? member.hpCurrent, member.hpCurrent), nextMax);
    const optimisticMember = {
      ...member,
      hpCurrent: nextCurrent,
      hpMax: nextMax,
      updatedAt: new Date().toISOString(),
    };

    setGroupMembers((current) =>
      current.map((entry) => (entry.groupMemberId === groupMemberId ? optimisticMember : entry))
    );

    try {
      const response = await fetch(
        `${groupApiUrl}/groups/${encodeURIComponent(normalizedGroupCode)}/characters/${encodeURIComponent(groupMemberId)}/hp`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(authSession ? { Authorization: `Bearer ${authSession.idToken}` } : {}),
          },
          body: JSON.stringify({ hpCurrent: nextCurrent, hpMax: nextMax }),
        }
      );

      if (response.status === 401) {
        setGroupStatus('Sign in again before tracking HP.');
        return;
      }
      if (!response.ok) throw new Error('HP update failed.');

      const data = (await response.json()) as { character?: GroupMember };
      if (data.character) {
        setGroupMembers((current) =>
          current.map((entry) => (entry.groupMemberId === groupMemberId ? data.character ?? entry : entry))
        );
      }
      setGroupStatus(`${member.name || 'Character'} HP updated`);
    } catch {
      setGroupStatus('Could not update HP right now.');
      await loadGroup(normalizedGroupCode);
    }
  };

  const uploadPortrait = async (file: File | null) => {
    if (!file) return;
    try {
      const portraitDataUrl = await resizeImage(file);
      update('portraitDataUrl', portraitDataUrl);
    } catch {
      setGroupStatus('That image could not be uploaded.');
    }
  };

  const showPasswordReset = () => {
    setAuthMode('resetRequest');
    setAuthError('');
    setAuthNotice('');
    setAuthPassword('');
    setAuthNewPassword('');
    setAuthResetCode('');
    setAuthChallenge(null);
  };

  const showSignIn = () => {
    setAuthMode('signIn');
    setAuthError('');
    setAuthNotice('');
    setAuthNewPassword('');
    setAuthResetCode('');
    setAuthChallenge(null);
  };

  const submitAuth = async () => {
    setAuthError('');
    setAuthNotice('');
    setAuthBusy(true);

    try {
      if (authMode === 'resetRequest') {
        const email = await requestPasswordReset(authEmail);
        setAuthEmail(email);
        setAuthMode('resetConfirm');
        setAuthNotice('Check your email for the reset code.');
        return;
      }

      if (authMode === 'resetConfirm') {
        const email = await confirmPasswordReset(authEmail, authResetCode, authNewPassword);
        setAuthEmail(email);
        setAuthMode('signIn');
        setAuthPassword('');
        setAuthNewPassword('');
        setAuthResetCode('');
        setAuthChallenge(null);
        setAuthNotice('Password updated. Sign in with your new password.');
        return;
      }

      const session = authChallenge
        ? await completeNewPassword(authChallenge, authNewPassword)
        : await signIn(authEmail, authPassword).then((result) => {
            if (result.kind === 'newPasswordRequired') {
              setAuthChallenge(result.challenge);
              setAuthEmail(result.challenge.email);
              setAuthNewPassword('');
              throw new Error('Create a permanent password to finish your invitation.');
            }
            return result.session;
          });

      setAuthSession(session);
      setAuthMode('signIn');
      setAuthNotice('');
      setAuthPassword('');
      setAuthNewPassword('');
      setAuthResetCode('');
      setAuthChallenge(null);
    } catch (error: unknown) {
      setAuthError(error instanceof Error ? error.message : 'Sign in could not be started.');
    } finally {
      setAuthBusy(false);
    }
  };

  if (authConfig.enabled && !authReady) {
    return <InviteOnlyGate status="Checking your invitation..." />;
  }

  if (authConfig.enabled && !authSession) {
    return (
      <InviteOnlyGate
        email={authEmail}
        password={authPassword}
        newPassword={authNewPassword}
        resetCode={authResetCode}
        mode={authMode}
        needsNewPassword={Boolean(authChallenge)}
        busy={authBusy}
        error={authError}
        notice={authNotice}
        onEmailChange={(value) => {
          setAuthEmail(value);
          if (authMode === 'signIn') setAuthChallenge(null);
        }}
        onPasswordChange={setAuthPassword}
        onNewPasswordChange={setAuthNewPassword}
        onResetCodeChange={setAuthResetCode}
        onForgotPassword={showPasswordReset}
        onBackToSignIn={showSignIn}
        onSubmit={submitAuth}
      />
    );
  }

  if (viewMode === 'dm') {
    return (
      <main className="app-shell dm-shell">
        <div className="sheet-toolbar" aria-label="Dungeon master tools">
          <div>
            <button className="text-button" type="button" onClick={() => setViewMode('library')}>
              <Users size={17} /> Characters
            </button>
            <p className="eyebrow">Group Command Center</p>
            <h1>Dungeon Master View</h1>
          </div>
          <div className="toolbar-actions">
            {authSession && (
              <button className="icon-button" type="button" onClick={signOut} aria-label={`Sign out${authSession.email ? ` ${authSession.email}` : ''}`}>
                <LogOut size={18} />
              </button>
            )}
          </div>
        </div>

        <section className="dm-dashboard" aria-label="Dungeon master dashboard">
          <section className="dm-party-panel">
            <GroupPanel
              groupCode={groupCode}
              setGroupCode={setGroupCode}
              groupMembers={groupMembers}
              groupStatus={groupStatus}
              joinGroup={joinGroup}
              loadGroup={() => loadGroup()}
              clearGroup={clearGroup}
              shareToGroup={shareToGroup}
              canShare={false}
              canManageHp
              onUpdateHp={updateGroupMemberHp}
            />
          </section>

          <section className="dm-tools-panel">
            <Panel title="DM Questions & Notes" icon={<BookOpen size={18} />}>
              <textarea
                className="dm-notes"
                placeholder="Questions for players, secret clues, initiative notes, or reminders for this group."
                value={dmNotes}
                onChange={(event) => updateDmNotes(event.target.value)}
              />
            </Panel>
            <ReferenceSearch
              filteredReference={filteredReference}
              search={search}
              setSearch={setSearch}
              setSelectedHelp={setSelectedHelp}
            />
            <FloatingAiAssistant
              sessions={chatSessions}
              activeChatId={activeChatId}
              messages={chatMessages}
              input={chatInput}
              busy={chatBusy}
              error={chatError}
              budget={chatBudget}
              setInput={setChatInput}
              onSubmit={askAiGuide}
              onApplyPatch={applyCharacterPatch}
              onSubmitChoices={sendAiGuideQuestion}
              onPhotoUpload={importSheetPhoto}
              onNewChat={startNewChat}
              onDeleteChat={deleteChat}
              onSelectChat={selectChat}
            />
          </section>
        </section>

        <HelpDrawer selectedHelp={selectedHelp} setSelectedHelp={setSelectedHelp} />
        <div className="save-indicator">
          <Save size={15} /> {authSession ? cloudStatus : 'Saved locally'}
        </div>
      </main>
    );
  }

  if (viewMode === 'library' || characters.length === 0) {
    return (
      <main className="app-shell library-shell">
        <section className="library-hero">
          <div>
            <p className="eyebrow">D&D Character Organizer</p>
            <h1>Choose Your Character</h1>
            <p className="library-copy">
              Pick a saved sheet, create a new adventurer, or open the rules reference before game night starts.
            </p>
          </div>
          <div className="library-actions">
            <button className="primary-action" type="button" onClick={addCharacter}>
              <UserRoundPlus size={20} /> New Character
            </button>
            <button className="text-button dm-view-button" type="button" onClick={openDmView}>
              <Shield size={18} /> Dungeon Master View
            </button>
          </div>
        </section>

        <AccountPanel session={authSession} onSignOut={signOut} />

        <section className="character-library" aria-label="Saved characters">
          {characters.length ? (
            characters.map((entry) => (
              <button className="character-card" key={entry.id} type="button" onClick={() => selectCharacter(entry.id)}>
                <span className="character-token">
                  {entry.portraitDataUrl ? <img src={entry.portraitDataUrl} alt="" /> : <Users size={22} />}
                </span>
                <strong>{entry.name || 'Unnamed Adventurer'}</strong>
                <span>{entry.classLevel || 'Class pending'}</span>
                <small>
                  {entry.race || 'Race pending'} / {entry.alignment || 'Alignment pending'}
                </small>
                <b>
                  AC {entry.armorClass} / HP {entry.hpCurrent}/{entry.hpMax}
                </b>
              </button>
            ))
          ) : (
            <article className="empty-library ink-card">
              <h2>No Characters Saved</h2>
              <p>Create a new adventurer when you are ready.</p>
              <button className="primary-action" type="button" onClick={addCharacter}>
                <UserRoundPlus size={20} /> New Character
              </button>
            </article>
          )}
        </section>

        <GroupPanel
          groupCode={groupCode}
          setGroupCode={setGroupCode}
          groupMembers={groupMembers}
          groupStatus={groupStatus}
          joinGroup={joinGroup}
          loadGroup={() => loadGroup()}
          clearGroup={clearGroup}
          shareToGroup={shareToGroup}
          canShare={false}
        />

        <ReferenceSearch
          filteredReference={filteredReference}
          search={search}
          setSearch={setSearch}
          setSelectedHelp={setSelectedHelp}
        />
        <FloatingAiAssistant
          sessions={chatSessions}
          activeChatId={activeChatId}
          messages={chatMessages}
          input={chatInput}
          busy={chatBusy}
          error={chatError}
          budget={chatBudget}
          setInput={setChatInput}
          onSubmit={askAiGuide}
          onApplyPatch={applyCharacterPatch}
          onSubmitChoices={sendAiGuideQuestion}
          onPhotoUpload={importSheetPhoto}
          onNewChat={startNewChat}
          onDeleteChat={deleteChat}
          onSelectChat={selectChat}
        />
        <DeleteCharacterDialog
          character={deleteTarget}
          typedName={deleteConfirmName}
          onTypedNameChange={setDeleteConfirmName}
          onCancel={cancelDeleteCharacter}
          onConfirm={confirmDeleteCharacter}
        />
        <HelpDrawer selectedHelp={selectedHelp} setSelectedHelp={setSelectedHelp} />
      </main>
    );
  }

  return (
    <main className="app-shell">
      <div className="sheet-toolbar" aria-label="Character sheet tools">
        <div>
          <button className="text-button" type="button" onClick={() => setViewMode('library')}>
            <Users size={17} /> Characters
          </button>
          <p className="eyebrow">Interactive Character Organizer</p>
          <h1>{character.name || 'Unnamed Adventurer'}</h1>
        </div>
        <div className="toolbar-actions">
          {authSession && (
            <button className="icon-button" type="button" onClick={signOut} aria-label={`Sign out${authSession.email ? ` ${authSession.email}` : ''}`}>
              <LogOut size={18} />
            </button>
          )}
          <button
            className="icon-button"
            type="button"
            onClick={() => void saveActiveCharacterToCloud('manual')}
            disabled={cloudSaveBusy || (Boolean(authSession) && !cloudReady)}
            aria-label="Save character to cloud"
            title="Save character to cloud"
          >
            <Save size={18} />
          </button>
          <button className="icon-button" type="button" onClick={exportSheet} aria-label="Export character">
            <Download size={18} />
          </button>
          <button className="icon-button danger-icon" type="button" onClick={requestDeleteCharacter} aria-label={`Delete ${character.name || 'character'}`}>
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      <nav className="mobile-tabs" aria-label="Character sections">
        {[
          ['stats', 'Stats'],
          ['combat', 'Combat'],
          ['story', 'Story'],
          ['inventory', 'Gear'],
          ['features', 'Features'],
          ['party', 'Party'],
        ].map(([tab, label]) => (
          <button
            className={activeTab === tab ? 'active' : ''}
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab as SheetTab)}
          >
            {label}
          </button>
        ))}
      </nav>

      <section className={`sheet-page ${activeTab === 'party' ? 'mobile-empty-tab' : ''}`} aria-label="Interactive character sheet">
        <header className={`character-banner mobile-section ${activeTab === 'stats' ? 'show' : ''}`}>
          <label className="portrait-uploader ink-card">
            <span>{character.portraitDataUrl ? <img src={character.portraitDataUrl} alt="" /> : <Upload size={28} />}</span>
            <strong>Portrait</strong>
            <input type="file" accept="image/*" onChange={(event) => uploadPortrait(event.target.files?.[0] ?? null)} />
          </label>
          <Field label="Character Name" value={character.name} onChange={(value) => update('name', value)} help={helpFor('Character Name')} onHelp={setSelectedHelp} large />
          <div className="identity-grid">
            <ClassLevelField
              value={character.classLevel}
              onClassSelect={applyClassSelection}
              onLevelSelect={applyLevelSelection}
              onManualClassChange={applyManualClassName}
              help={helpFor('Class')}
              onHelp={setSelectedHelp}
            />
            <ChoiceField
              label="Background"
              value={character.background}
              options={backgroundRules}
              onSelect={applyBackgroundSelection}
              onManualChange={(value) => update('background', value)}
              help={helpFor('Background')}
              onHelp={setSelectedHelp}
            />
            <Field label="Player Name" value={character.player} onChange={(value) => update('player', value)} />
            <ChoiceField
              label="Race"
              value={character.race}
              options={raceRules}
              onSelect={applyRaceSelection}
              onManualChange={(value) => update('race', value)}
              help={helpFor('Race')}
              onHelp={setSelectedHelp}
            />
            <ChoiceField
              label="Alignment"
              value={character.alignment}
              options={alignmentRules}
              onSelect={applyAlignmentSelection}
              onManualChange={(value) => update('alignment', value)}
              help={helpFor('Alignment')}
              onHelp={setSelectedHelp}
            />
            <Field label="Experience Points" value={character.xp} onChange={(value) => update('xp', value)} />
            <RuleWarnings warnings={ruleWarnings} />
          </div>
        </header>

        <section className={`sheet-column abilities-column mobile-section ${activeTab === 'stats' ? 'show' : ''}`}>
          <div className="ability-stack">
            {character.abilities.map((ability) => (
              <article className="ability-card ink-card" key={ability.key}>
                <div className="ability-heading">
                  <h2>{ability.name}</h2>
                  <button className="help-chip" type="button" onClick={() => openHelp(ability.name, ability.name)} aria-label={`${ability.name} help`}>
                    <Info size={13} />
                  </button>
                </div>
                <div className="ability-mod-value">
                  <strong>{formatMod(modifier(ability.score))}</strong>
                  <span>Modifier</span>
                </div>
                <label className="ability-score-field">
                  <input
                    aria-label={`${ability.name} score`}
                    type="number"
                    value={ability.score}
                    onChange={(event) => updateAbility(ability.key, Number(event.target.value))}
                  />
                  <span>Score</span>
                </label>
              </article>
            ))}
          </div>
        </section>

        <section className={`sheet-column skills-column mobile-section ${activeTab === 'stats' ? 'show' : ''}`}>
          <section className="sheet-ribbon-stack ink-card" aria-label="Proficiency bonus">
            <div className="sheet-ribbon proficiency-bonus-ribbon">
              <input
                id="proficiency-bonus-input"
                aria-label="Proficiency bonus"
                type="number"
                value={character.proficiencyBonus}
                onChange={(event) => update('proficiencyBonus', Number(event.target.value))}
              />
              <label htmlFor="proficiency-bonus-input">Proficiency Bonus</label>
              {proficiencyBonusHelp && (
                <button
                  className="panel-help"
                  type="button"
                  onClick={() => setSelectedHelp(proficiencyBonusHelp)}
                  aria-label="Proficiency bonus help"
                >
                  <Info size={13} />
                </button>
              )}
            </div>
          </section>
          <Panel title="Saving Throws" icon={<Shield size={18} />} help={helpFor('Saving Throws')} onHelp={setSelectedHelp}>
            {character.abilities.map((ability) => {
              const proficient = character.savingThrowProficiencies?.[ability.key] ?? false;
              const calculatedValue = formatMod(abilityMods[ability.key] + (proficient ? character.proficiencyBonus : 0));
              const savedValue = character.savingThrowOverrides?.[ability.key] ?? calculatedValue;
              return (
                <CheckRow
                  key={ability.key}
                  label={ability.name}
                  value={savedValue}
                  proficient={proficient}
                  onValueChange={(value) => updateSavingThrowOverride(ability.key, value)}
                  onToggleProficiency={() => updateSavingThrowProficiency(ability.key, !proficient)}
                />
              );
            })}
          </Panel>
          <Panel title="Skills" icon={<BookOpen size={18} />} help={helpFor('Skills')} onHelp={setSelectedHelp}>
            <div className="skill-list">
              {character.skills.map((skill) => {
                const total =
                  abilityMods[skill.ability] +
                  (skill.proficient ? character.proficiencyBonus : 0) +
                  (skill.expertise ? character.proficiencyBonus : 0);
                const savedValue = character.skillOverrides?.[skill.name] ?? formatMod(total);
                return (
                  <div className="skill-row" key={skill.name}>
                    <button
                      className={skill.proficient ? 'sheet-checkbox checked' : 'sheet-checkbox'}
                      type="button"
                      onClick={() =>
                        updateSkill(skill.name, {
                          proficient: !skill.proficient,
                          expertise: skill.proficient ? false : skill.expertise,
                        })
                      }
                      aria-pressed={skill.proficient}
                      aria-label={`Toggle ${skill.name} proficiency`}
                      title="Proficient"
                    />
                    <input
                      className="bonus-input"
                      aria-label={`${skill.name} bonus`}
                      value={savedValue}
                      onChange={(event) => updateSkillOverride(skill.name, event.target.value)}
                    />
                    <span className="skill-name">
                      {skill.name} <small>({abilityLabels[skill.ability].slice(0, 3)})</small>
                    </span>
                  </div>
                );
              })}
            </div>
          </Panel>
        </section>

        <section className={`sheet-column center-column mobile-section ${activeTab === 'combat' ? 'show' : ''}`}>
          <div className="quick-stats">
            <NumberBadge label="Armor Class" value={character.armorClass} onChange={(value) => update('armorClass', value)} icon={<Shield size={18} />} />
            <NumberBadge label="Initiative" value={character.initiative} onChange={(value) => update('initiative', value)} icon={<Sparkles size={18} />} />
            <NumberBadge label="Speed" value={character.speed} onChange={(value) => update('speed', value)} icon={<ChevronDown size={18} />} />
          </div>
          <Panel title="Hit Points" icon={<Heart size={18} />} help={helpFor('Hit Points')} onHelp={setSelectedHelp}>
            <div className="hp-box">
              <label className="hp-side-stat">
                <span>Max HP</span>
                <input type="number" value={character.hpMax} onChange={(event) => update('hpMax', Number(event.target.value))} />
              </label>
              <div className="hp-current">
                <span className="hp-current-label">Current HP</span>
                <div className="hp-current-controls">
                  <button type="button" onClick={() => update('hpCurrent', Math.max(0, character.hpCurrent - 1))} aria-label="Take 1 damage" title="Take damage">
                    <Minus size={18} />
                  </button>
                  <input
                    aria-label="Current hit points"
                    min="0"
                    type="number"
                    value={character.hpCurrent}
                    onChange={(event) => update('hpCurrent', Math.max(0, Number(event.target.value)))}
                  />
                  <button
                    type="button"
                    onClick={() => update('hpCurrent', character.hpMax > 0 ? Math.min(character.hpMax, character.hpCurrent + 1) : character.hpCurrent + 1)}
                    aria-label="Heal 1 hit point"
                    title="Heal"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>
              <label className="hp-side-stat">
                <span>Temp HP</span>
                <input type="number" value={character.hpTemp} onChange={(event) => update('hpTemp', Number(event.target.value))} />
              </label>
              <p className="hp-helper">Damage lowers Current HP. Temp HP is a separate buffer used before Current HP.</p>
            </div>
          </Panel>
          <div className="split-panels">
            <Panel title="Hit Dice" help={helpFor('Hit Dice')} onHelp={setSelectedHelp}>
              <Field label="Total" value={character.hitDice} onChange={(value) => update('hitDice', value)} compact />
            </Panel>
            <Panel title="Death Saves" help={helpFor('Death Saves')} onHelp={setSelectedHelp}>
              <Pips label="Successes" value={character.deathSuccesses} onChange={(value) => update('deathSuccesses', value)} />
              <Pips label="Failures" value={character.deathFailures} onChange={(value) => update('deathFailures', value)} />
            </Panel>
          </div>
          <Panel title="Attacks & Spellcasting" icon={<Sword size={18} />} help={helpFor('Attacks & Spellcasting')} onHelp={setSelectedHelp}>
            <div className="weapon-table">
              {character.weapons.map((weapon, index) => (
                <div className="weapon-row" key={`weapon-row-${index}`}>
                  <input className="weapon-name" aria-label="Weapon name" placeholder="Weapon" value={weapon.name} onChange={(event) => updateWeapon(index, { name: event.target.value })} />
                  <input className="weapon-bonus" aria-label="Attack bonus" placeholder="Bonus" value={weapon.bonus} onChange={(event) => updateWeapon(index, { bonus: event.target.value })} />
                  <input className="weapon-damage" aria-label="Damage" placeholder="Damage" value={weapon.damage} onChange={(event) => updateWeapon(index, { damage: event.target.value })} />
                </div>
              ))}
            </div>
            <textarea
              className="attack-notes"
              aria-label="Attack and spellcasting notes"
              placeholder="Ammo, spell save DC, spell attack bonus, limited-use attacks, or table reminders."
              value={character.attackNotes}
              onChange={(event) => update('attackNotes', event.target.value)}
            />
          </Panel>
        </section>

        <section className={`sheet-column right-column mobile-section ${activeTab === 'story' ? 'show' : ''}`}>
          <Panel title="Personality Traits" help={helpFor('Personality Traits')} onHelp={setSelectedHelp}><Textarea value={character.traits} onChange={(value) => update('traits', value)} /></Panel>
          <Panel title="Ideals" help={helpFor('Ideals')} onHelp={setSelectedHelp}><Textarea value={character.ideals} onChange={(value) => update('ideals', value)} /></Panel>
          <Panel title="Bonds" help={helpFor('Bonds')} onHelp={setSelectedHelp}><Textarea value={character.bonds} onChange={(value) => update('bonds', value)} /></Panel>
          <Panel title="Flaws" help={helpFor('Flaws')} onHelp={setSelectedHelp}><Textarea value={character.flaws} onChange={(value) => update('flaws', value)} /></Panel>
        </section>

        <section className={`passive-section mobile-section ${activeTab === 'inventory' ? 'show' : ''}`}>
          <Panel title="Passive Wisdom (Perception)" help={helpFor('Passive Wisdom')} onHelp={setSelectedHelp}>
            <input className="single-number" type="number" value={character.passivePerception} onChange={(event) => update('passivePerception', Number(event.target.value))} />
          </Panel>
        </section>

        <section className={`proficiency-section mobile-section ${activeTab === 'inventory' ? 'show' : ''}`}>
          <Panel title="Other Proficiencies & Languages" help={helpFor('Other Proficiencies & Languages')} onHelp={setSelectedHelp}>
            <Textarea value={character.proficiencies} onChange={(value) => update('proficiencies', value)} tall />
          </Panel>
        </section>

        <section className={`bottom-center mobile-section ${activeTab === 'inventory' ? 'show' : ''}`}>
          <Panel title="Equipment" help={helpFor('Equipment')} onHelp={setSelectedHelp}>
            <Textarea value={character.equipment} onChange={(value) => update('equipment', value)} tall />
          </Panel>
        </section>

        <section className={`feature-column mobile-section ${activeTab === 'features' ? 'show' : ''}`}>
          <Panel title="Features & Traits" help={helpFor('Features & Traits')} onHelp={setSelectedHelp}>
            <Textarea value={character.features} onChange={(value) => update('features', value)} tall />
          </Panel>
        </section>
      </section>

      <section className={`second-page mobile-section ${activeTab === 'story' ? 'show' : ''}`} aria-label="Character notes">
        <Panel title="Appearance" help={helpFor('Appearance')} onHelp={setSelectedHelp}><Textarea value={character.appearance} onChange={(value) => update('appearance', value)} tall /></Panel>
        <Panel title="Allies & Organizations" help={helpFor('Allies & Organizations')} onHelp={setSelectedHelp}><Textarea value={character.allies} onChange={(value) => update('allies', value)} tall /></Panel>
        <Panel title="Character Backstory" help={helpFor('Character Backstory')} onHelp={setSelectedHelp}><Textarea value={character.backstory} onChange={(value) => update('backstory', value)} tall /></Panel>
        <Panel title="Treasure" help={helpFor('Treasure')} onHelp={setSelectedHelp}><Textarea value={character.treasure} onChange={(value) => update('treasure', value)} tall /></Panel>
      </section>

      <section className="sheet-tools-row" aria-label="Party and reference tools">
        <section className={`party-section mobile-section ${activeTab === 'party' ? 'show' : ''}`}>
          <GroupPanel
            groupCode={groupCode}
            setGroupCode={setGroupCode}
            groupMembers={groupMembers}
            groupStatus={groupStatus}
            joinGroup={joinGroup}
            loadGroup={() => loadGroup()}
            clearGroup={clearGroup}
            shareToGroup={shareToGroup}
            canShare
          />
        </section>

        <section className={`reference-section mobile-section ${activeTab === 'features' ? 'show' : ''}`}>
          <ReferenceSearch
            filteredReference={filteredReference}
            search={search}
            setSearch={setSearch}
            setSelectedHelp={setSelectedHelp}
          />
        </section>

      </section>

      <HelpDrawer
        selectedHelp={selectedHelp}
        personalNote={selectedHelp ? character.personalNotes?.[selectedHelp.key] ?? '' : ''}
        setPersonalNote={(value) =>
          update('personalNotes', {
            ...(character.personalNotes ?? {}),
            [selectedHelp?.key ?? 'general']: value,
          })
        }
        setSelectedHelp={setSelectedHelp}
      />

      <DeleteCharacterDialog
        character={deleteTarget}
        typedName={deleteConfirmName}
        onTypedNameChange={setDeleteConfirmName}
        onCancel={cancelDeleteCharacter}
        onConfirm={confirmDeleteCharacter}
      />

      <FloatingAiAssistant
        sessions={chatSessions}
        activeChatId={activeChatId}
        messages={chatMessages}
        input={chatInput}
        busy={chatBusy}
        error={chatError}
        budget={chatBudget}
        setInput={setChatInput}
        onSubmit={askAiGuide}
        onApplyPatch={applyCharacterPatch}
        onSubmitChoices={sendAiGuideQuestion}
        onPhotoUpload={importSheetPhoto}
        onNewChat={startNewChat}
        onDeleteChat={deleteChat}
        onSelectChat={selectChat}
      />

      <div className="save-indicator">
        <Save size={15} /> {authSession ? cloudStatus : 'Saved locally'}
      </div>
    </main>
  );
}

function InviteOnlyGate({
  email,
  password,
  newPassword,
  resetCode,
  mode = 'signIn',
  needsNewPassword,
  busy,
  error,
  notice,
  status,
  onEmailChange,
  onPasswordChange,
  onNewPasswordChange,
  onResetCodeChange,
  onForgotPassword,
  onBackToSignIn,
  onSubmit,
}: {
  email?: string;
  password?: string;
  newPassword?: string;
  resetCode?: string;
  mode?: AuthMode;
  needsNewPassword?: boolean;
  busy?: boolean;
  error?: string;
  notice?: string;
  status?: string;
  onEmailChange?: (value: string) => void;
  onPasswordChange?: (value: string) => void;
  onNewPasswordChange?: (value: string) => void;
  onResetCodeChange?: (value: string) => void;
  onForgotPassword?: () => void;
  onBackToSignIn?: () => void;
  onSubmit?: () => void;
}) {
  const isResetRequest = mode === 'resetRequest';
  const isResetConfirm = mode === 'resetConfirm';
  const buttonText = busy
    ? isResetRequest || isResetConfirm
      ? 'Sending...'
      : 'Checking...'
    : isResetRequest
      ? 'Send Reset Code'
      : isResetConfirm
        ? 'Set New Password'
        : needsNewPassword
          ? 'Set Password'
          : 'Sign In';

  return (
    <main className="app-shell auth-shell">
      <section className="auth-card ink-card" aria-labelledby="auth-title">
        <div className="auth-story">
          <span className="auth-mark">
            <Mail size={30} />
          </span>
          <p className="eyebrow">Invite Only</p>
          <h1 id="auth-title">D&D Character Organizer</h1>
          <p className="auth-copy">
            Private sheets, party groups, table notes, and rules help for invited players.
          </p>
          <span className="auth-email-chip">Email invitation required</span>
        </div>

        {onSubmit ? (
          <form
            className="auth-form"
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
          >
            <label>
              <span>Email</span>
              <input
                autoComplete="username"
                inputMode="email"
                placeholder="you@example.com"
                type="email"
                value={email}
                onChange={(event) => onEmailChange?.(event.target.value)}
              />
            </label>
            {!isResetRequest && !isResetConfirm && (
              <label>
                <span>{needsNewPassword ? 'Temporary Password' : 'Password'}</span>
                <input
                  autoComplete="current-password"
                  type="password"
                  value={password}
                  onChange={(event) => onPasswordChange?.(event.target.value)}
                />
              </label>
            )}
            {isResetConfirm && (
              <label>
                <span>Reset Code</span>
                <input
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  value={resetCode}
                  onChange={(event) => onResetCodeChange?.(event.target.value)}
                />
              </label>
            )}
            {needsNewPassword && (
              <label>
                <span>New Password</span>
                <input
                  autoComplete="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(event) => onNewPasswordChange?.(event.target.value)}
                />
              </label>
            )}
            {isResetConfirm && (
              <label>
                <span>New Password</span>
                <input
                  autoComplete="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(event) => onNewPasswordChange?.(event.target.value)}
                />
              </label>
            )}
            <button className="primary-action" type="submit" disabled={busy}>
              <LogIn size={20} /> {buttonText}
            </button>
            {!needsNewPassword && mode === 'signIn' && (
              <button className="auth-link-button" type="button" onClick={onForgotPassword}>
                Forgot password?
              </button>
            )}
            {(isResetRequest || isResetConfirm || needsNewPassword) && (
              <button className="auth-link-button" type="button" onClick={onBackToSignIn}>
                Back to sign in
              </button>
            )}
            {notice && <p className="auth-status">{notice}</p>}
            {error && <p className={needsNewPassword ? 'auth-status' : 'auth-error'}>{error}</p>}
          </form>
        ) : (
          <p className="auth-status">{status}</p>
        )}
      </section>
    </main>
  );
}

function AccountPanel({ session, onSignOut }: { session: AuthSession | null; onSignOut: () => void }) {
  if (!session) return null;

  return (
    <section className="account-panel ink-card" aria-label="Account">
      <div>
        <p className="eyebrow">Account</p>
        <h2>Signed In</h2>
        <p>{session.email ?? 'Invited player'}</p>
      </div>
      <button className="text-button sign-out-action" type="button" onClick={onSignOut}>
        <LogOut size={18} /> Sign Out
      </button>
    </section>
  );
}

function DeleteCharacterDialog({
  character,
  typedName,
  onTypedNameChange,
  onCancel,
  onConfirm,
}: {
  character: Character | null;
  typedName: string;
  onTypedNameChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!character) return null;

  const characterName = character.name.trim() || 'Unnamed Adventurer';
  const canConfirm = typedName.trim() === characterName;

  return (
    <div className="delete-dialog" role="dialog" aria-modal="true" aria-labelledby="delete-character-title">
      <button className="scrim" type="button" aria-label="Cancel delete character" onClick={onCancel} />
      <article>
        <button className="close icon-button" type="button" onClick={onCancel} aria-label="Close delete confirmation">
          <X size={18} />
        </button>
        <span className="danger-mark">
          <Trash2 size={24} />
        </span>
        <p className="eyebrow">Delete Character</p>
        <h2 id="delete-character-title">{characterName}</h2>
        <p>
          This removes the saved character from this device. Type the character name below to confirm.
        </p>
        <label>
          <span>Type {characterName}</span>
          <input
            autoFocus
            value={typedName}
            onChange={(event) => onTypedNameChange(event.target.value)}
          />
        </label>
        <div className="delete-actions">
          <button className="text-button" type="button" onClick={onCancel}>
            Cancel
          </button>
          <button className="primary-action danger-action" type="button" onClick={onConfirm} disabled={!canConfirm}>
            <Trash2 size={18} /> Delete Character
          </button>
        </div>
      </article>
    </div>
  );
}

function GroupPanel({
  groupCode,
  setGroupCode,
  groupMembers,
  groupStatus,
  joinGroup,
  loadGroup,
  clearGroup,
  shareToGroup,
  canShare,
  canManageHp = false,
  onUpdateHp,
}: {
  groupCode: string;
  setGroupCode: (value: string) => void;
  groupMembers: GroupMember[];
  groupStatus: string;
  joinGroup: () => void;
  loadGroup: () => void;
  clearGroup: () => void;
  shareToGroup: () => void;
  canShare: boolean;
  canManageHp?: boolean;
  onUpdateHp?: (member: GroupMember, patch: Partial<Pick<GroupMember, 'hpCurrent' | 'hpMax'>>) => void;
}) {
  const hasGroupCode = groupCode.trim().length > 0;

  return (
    <Panel title="Group" icon={<Users size={18} />}>
      <div className="group-controls">
        <input
          aria-label="Group code"
          placeholder="Group code"
          value={groupCode}
          onChange={(event) => setGroupCode(event.target.value.toUpperCase())}
        />
        <button type="button" onClick={joinGroup}>Join</button>
        <button type="button" onClick={loadGroup}>Refresh</button>
        {canShare && <button type="button" onClick={shareToGroup}>Share</button>}
        {(hasGroupCode || groupMembers.length > 0 || groupStatus) && (
          <button type="button" onClick={clearGroup}>Clear</button>
        )}
      </div>
      {groupStatus && <p className="group-status">{groupStatus}</p>}
      <div className="group-roster">
        {groupMembers.length === 0 ? (
          <p>{hasGroupCode ? 'No shared characters yet.' : 'Enter a group code to see shared characters.'}</p>
        ) : (
          groupMembers.map((member) => (
            <article key={member.id}>
              <span>{member.portraitDataUrl ? <img src={member.portraitDataUrl} alt="" /> : <Users size={17} />}</span>
              <div className="group-member-summary">
                <strong>{member.name || 'Unnamed Adventurer'}</strong>
                <small>{member.classLevel || 'Class pending'} / AC {member.armorClass} / HP {member.hpCurrent}/{member.hpMax}</small>
                {canManageHp && (
                  <div className="group-hp-controls" aria-label={`Track hit points for ${member.name || 'character'}`}>
                    <button
                      type="button"
                      onClick={() => onUpdateHp?.(member, { hpCurrent: member.hpCurrent - 1 })}
                      disabled={!member.groupMemberId}
                      aria-label={`Decrease ${member.name || 'character'} hit points`}
                    >
                      -
                    </button>
                    <label>
                      <span>HP</span>
                      <input
                        type="number"
                        value={member.hpCurrent}
                        onChange={(event) => onUpdateHp?.(member, { hpCurrent: Number(event.target.value) })}
                        disabled={!member.groupMemberId}
                      />
                    </label>
                    <span className="hp-divider">/</span>
                    <label>
                      <span>Max</span>
                      <input
                        type="number"
                        value={member.hpMax}
                        onChange={(event) => onUpdateHp?.(member, { hpMax: Number(event.target.value) })}
                        disabled={!member.groupMemberId}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => onUpdateHp?.(member, { hpCurrent: member.hpCurrent + 1 })}
                      disabled={!member.groupMemberId}
                      aria-label={`Increase ${member.name || 'character'} hit points`}
                    >
                      +
                    </button>
                  </div>
                )}
              </div>
            </article>
          ))
        )}
      </div>
    </Panel>
  );
}

function FloatingAiAssistant({
  sessions,
  activeChatId,
  messages,
  input,
  busy,
  error,
  budget,
  setInput,
  onSubmit,
  onApplyPatch,
  onSubmitChoices,
  onPhotoUpload,
  onNewChat,
  onDeleteChat,
  onSelectChat,
}: {
  sessions: ChatSession[];
  activeChatId: string;
  messages: ChatMessage[];
  input: string;
  busy: boolean;
  error: string;
  budget: ChatBudget | null;
  setInput: (value: string) => void;
  onSubmit: () => void;
  onApplyPatch: (patch?: CharacterPatch | null) => void;
  onSubmitChoices: (value: string) => void;
  onPhotoUpload: (file: File | null) => void;
  onNewChat: () => void;
  onDeleteChat: () => void;
  onSelectChat: (id: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const starterQuestions = [
    'Interview me to make a new character. Ask race, class, background, ability scores, and gear before filling the sheet.',
    'Check this sheet for mistakes and prepare fixes I can apply.',
    'Help me make a fast sneaky archer. Recommend choices, but ask me what I want before deciding.',
  ];

  return (
    <aside className={isOpen ? 'floating-ai-assistant open' : 'floating-ai-assistant'}>
      {isOpen && (
        <section className="floating-ai-popup" role="dialog" aria-label="AI rules guide" aria-modal="false">
          <header className="floating-ai-header">
            <span className="assistant-avatar">
              <Bot size={21} />
            </span>
            <div>
              <strong>AI Rules Guide</strong>
              <small>Build help, rules, and sheet fixes</small>
            </div>
            <button
              className="icon-button"
              type="button"
              onClick={() => {
                setConfirmDelete(false);
                setIsOpen(false);
              }}
              aria-label="Close AI guide"
            >
              <X size={18} />
            </button>
          </header>

          <div className="floating-ai-body">
            <aside className="chat-history-panel" aria-label="Chat history">
              <div className="chat-history-heading">
                <strong>Chat History</strong>
                <small>{sessions.length} saved</small>
              </div>
              <div className="chat-history-list">
                {sessions.map((session) => (
                  <button
                    className={session.id === activeChatId ? 'active' : ''}
                    key={session.id}
                    type="button"
                    onClick={() => {
                      setConfirmDelete(false);
                      onSelectChat(session.id);
                    }}
                  >
                    <span>{session.title}</span>
                    <small>
                      {session.messages.length} {session.messages.length === 1 ? 'message' : 'messages'}
                    </small>
                  </button>
                ))}
              </div>
            </aside>

            <section className="chat-workspace">
              <div className="ai-budget" aria-label="AI usage budget">
                <span>
                  {budget
                    ? `${formatMicroUsd(budget.usedMicroUsd)} / ${formatMicroUsd(budget.limitMicroUsd)}`
                    : '$0.00 / $20.00'}
                </span>
                <strong>Hard stop at $20</strong>
              </div>
              <div className="chat-actions" aria-label="AI chat actions">
                <button type="button" onClick={() => {
                  setConfirmDelete(false);
                  onNewChat();
                }}>
                  <Plus size={16} /> New Chat
                </button>
                <button type="button" className="danger-action" onClick={() => setConfirmDelete(true)} disabled={messages.length === 0}>
                  <Trash2 size={16} /> Delete Chat
                </button>
              </div>
              {confirmDelete && (
                <div className="chat-delete-confirm" role="alert">
                  <strong>Delete this chat?</strong>
                  <div>
                    <button type="button" onClick={() => setConfirmDelete(false)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="danger-action"
                      onClick={() => {
                        onDeleteChat();
                        setConfirmDelete(false);
                      }}
                    >
                      Delete Chat
                    </button>
                  </div>
                </div>
              )}
              <div className="chat-prompts" aria-label="Suggested AI prompts">
                {starterQuestions.map((question) => (
                  <button key={question} type="button" onClick={() => setInput(question)}>
                    {question}
                  </button>
                ))}
              </div>
              <div className="chat-log" aria-live="polite">
                {messages.length === 0 ? (
                  <p className="chat-empty">Ask for a guided build, sheet math, rules help, or upload a hard-copy sheet photo.</p>
                ) : (
                  messages.map((message, index) => (
                    <article className={`chat-message ${message.role}`} key={`${message.role}-${index}`}>
                      <strong className="chat-message-role">{message.role === 'user' ? 'You' : 'Guide'}</strong>
                      <ChatMarkdown content={message.content} />
                      {message.builderChoices?.length ? (
                        <BuilderChoiceForm choices={message.builderChoices} busy={busy} onSubmit={onSubmitChoices} />
                      ) : null}
                      {message.patch && (
                        <button className="apply-patch-button" type="button" onClick={() => onApplyPatch(message.patch)}>
                          <Sparkles size={15} /> Apply to Sheet
                        </button>
                      )}
                      {message.sources?.length ? (
                        <details>
                          <summary>Sources</summary>
                          <ul>
                            {message.sources.slice(0, 4).map((source) => (
                              <li key={source}>{source}</li>
                            ))}
                          </ul>
                        </details>
                      ) : null}
                    </article>
                  ))
                )}
              </div>
              <form
                className="chat-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  onSubmit();
                }}
              >
                <textarea
                  aria-label="Ask the AI rules guide"
                  maxLength={1400}
                  placeholder="Ask a D&D question"
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                      event.preventDefault();
                      onSubmit();
                    }
                  }}
                />
                <button className="primary-action" type="submit" disabled={busy || !input.trim()}>
                  <SendHorizontal size={18} /> {busy ? 'Asking...' : 'Ask'}
                </button>
              </form>
              <label className="photo-import-button">
                <Upload size={17} /> Import Hard-Copy Sheet
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(event) => {
                    onPhotoUpload(event.target.files?.[0] ?? null);
                    event.currentTarget.value = '';
                  }}
                />
              </label>
              {error && <p className="auth-error chat-error">{error}</p>}
            </section>
          </div>
        </section>
      )}
      {!isOpen && (
        <button className="floating-ai-nudge" type="button" onClick={() => setIsOpen(true)}>
          <Sparkles size={15} /> Need help?
        </button>
      )}
      <button
        className="floating-ai-trigger"
        type="button"
        onClick={() => {
          setConfirmDelete(false);
          setIsOpen((current) => !current);
        }}
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Close AI guide' : 'Open AI guide'}
      >
        {isOpen ? <X size={24} /> : <Bot size={25} />}
        <span>{isOpen ? 'Close' : 'AI Guide'}</span>
        {busy && <i aria-label="AI guide is thinking" />}
      </button>
    </aside>
  );
}

const customChoiceValue = '__custom__';

function recommendedChoiceOption(choice: BuilderChoice) {
  const recommendation = choice.recommendation?.toLowerCase() ?? '';
  return choice.options.find((option) => recommendation.includes(option.toLowerCase())) ?? '';
}

function BuilderChoiceForm({
  choices,
  busy,
  onSubmit,
}: {
  choices: BuilderChoice[];
  busy: boolean;
  onSubmit: (value: string) => void;
}) {
  const [answers, setAnswers] = useState<Record<string, string>>(() =>
    Object.fromEntries(choices.map((choice) => [choice.id, recommendedChoiceOption(choice)]))
  );
  const [customAnswers, setCustomAnswers] = useState<Record<string, string>>({});

  const answerLines = choices
    .map((choice, index) => {
      const selected = answers[choice.id] ?? '';
      const value = selected === customChoiceValue ? customAnswers[choice.id]?.trim() ?? '' : selected;
      return value ? `${index + 1}. ${choice.label}: ${value}` : '';
    })
    .filter(Boolean);
  const canSubmit = answerLines.length > 0 && !busy;

  return (
    <form
      className="builder-choice-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSubmit) return;
        onSubmit(
          `Here are my character builder answers:\n${answerLines.join(
            '\n'
          )}\nUse these answers, remember them, and ask only the next missing character creation questions.`
        );
      }}
    >
      {choices.map((choice) => {
        const selected = answers[choice.id] ?? '';
        const customSelected = selected === customChoiceValue;

        return (
          <fieldset key={choice.id}>
            <legend>{choice.label}</legend>
            <label>
              <span>{choice.question}</span>
              <select
                value={selected}
                onChange={(event) => setAnswers((current) => ({ ...current, [choice.id]: event.target.value }))}
              >
                <option value="">Choose...</option>
                {choice.options.map((option) => (
                  <option key={option} value={option}>
                    {choice.recommendation?.toLowerCase().includes(option.toLowerCase()) ? `${option} (recommended)` : option}
                  </option>
                ))}
                {choice.allowCustom !== false && <option value={customChoiceValue}>{choice.customLabel ?? 'Other'}</option>}
              </select>
            </label>
            {choice.recommendation && <small>{choice.recommendation}</small>}
            {customSelected && (
              <input
                aria-label={`${choice.label} custom answer`}
                placeholder={choice.customLabel ?? 'Other'}
                value={customAnswers[choice.id] ?? ''}
                onChange={(event) => setCustomAnswers((current) => ({ ...current, [choice.id]: event.target.value }))}
              />
            )}
          </fieldset>
        );
      })}
      <button type="submit" disabled={!canSubmit}>
        Use Selected Answers
      </button>
    </form>
  );
}

function FieldLabel({
  label,
  help,
  onHelp,
}: {
  label: string;
  help?: ReferenceEntry;
  onHelp?: (entry: ReferenceEntry) => void;
}) {
  return (
    <span>
      {label}
      {help && onHelp && (
        <button type="button" onClick={() => onHelp(help)} aria-label={`${label} rules help`}>
          <Info size={13} />
        </button>
      )}
    </span>
  );
}

function ClassLevelField({
  value,
  onClassSelect,
  onLevelSelect,
  onManualClassChange,
  help,
  onHelp,
}: {
  value: string;
  onClassSelect: (id: string) => void;
  onLevelSelect: (level: number) => void;
  onManualClassChange: (value: string) => void;
  help?: ReferenceEntry;
  onHelp?: (entry: ReferenceEntry) => void;
}) {
  const selectedClass = classRuleFromClassLevel(value);
  const customClassName = selectedClass ? '' : classNameFromClassLevel(value);
  const [manualOpen, setManualOpen] = useState(false);
  const showManual = manualOpen || Boolean(customClassName);

  useEffect(() => {
    if (selectedClass) setManualOpen(false);
  }, [selectedClass?.id]);

  return (
    <label className="field class-level-field">
      <FieldLabel label="Class & Level" help={help} onHelp={onHelp} />
      <div className="choice-controls class-controls">
        <select
          aria-label="Class"
          value={selectedClass?.id ?? (showManual ? manualChoiceValue : '')}
          onChange={(event) => {
            const nextValue = event.target.value;
            if (nextValue === manualChoiceValue) {
              setManualOpen(true);
              return;
            }

            setManualOpen(false);
            if (nextValue) {
              onClassSelect(nextValue);
            } else {
              onManualClassChange('');
            }
          }}
        >
          <option value="">Choose class</option>
          {classRules.map((rule) => (
            <option key={rule.id} value={rule.id}>
              {rule.label}
            </option>
          ))}
          <option value={manualChoiceValue}>Other</option>
        </select>
        <select
          aria-label="Level"
          value={levelFromClassLevel(value)}
          onChange={(event) => onLevelSelect(Number(event.target.value))}
        >
          {levelOptions.map((level) => (
            <option key={level} value={level}>
              Level {level}
            </option>
          ))}
        </select>
      </div>
      {showManual && (
        <input
          aria-label="Custom class"
          placeholder="Custom class"
          value={customClassName}
          onChange={(event) => onManualClassChange(event.target.value)}
        />
      )}
    </label>
  );
}

function ChoiceField<T extends { id: string; label: string; aliases?: string[] }>({
  label,
  value,
  options,
  onSelect,
  onManualChange,
  help,
  onHelp,
}: {
  label: string;
  value: string;
  options: T[];
  onSelect: (id: string) => void;
  onManualChange: (value: string) => void;
  help?: ReferenceEntry;
  onHelp?: (entry: ReferenceEntry) => void;
}) {
  const selectedOption = choiceRuleFromValue(options, value);
  const [manualOpen, setManualOpen] = useState(false);
  const showManual = manualOpen || Boolean(value.trim() && !selectedOption);

  useEffect(() => {
    if (selectedOption) setManualOpen(false);
  }, [selectedOption?.id]);

  return (
    <label className="field choice-field">
      <FieldLabel label={label} help={help} onHelp={onHelp} />
      <select
        aria-label={label}
        value={selectedOption?.id ?? (showManual ? manualChoiceValue : '')}
        onChange={(event) => {
          const nextValue = event.target.value;
          if (nextValue === manualChoiceValue) {
            setManualOpen(true);
            return;
          }

          setManualOpen(false);
          if (nextValue) {
            onSelect(nextValue);
          } else {
            onManualChange('');
          }
        }}
      >
        <option value="">Choose {label.toLowerCase()}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
        <option value={manualChoiceValue}>Other</option>
      </select>
      {showManual && (
        <input
          aria-label={`Custom ${label}`}
          placeholder={`Custom ${label.toLowerCase()}`}
          value={selectedOption ? '' : value}
          onChange={(event) => onManualChange(event.target.value)}
        />
      )}
    </label>
  );
}

function RuleWarnings({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) return null;

  return (
    <div className="rules-hints" role="status" aria-label="Rules reminders">
      <strong>Rules Check</strong>
      <ul>
        {warnings.map((warning) => (
          <li key={warning}>{warning}</li>
        ))}
      </ul>
    </div>
  );
}

type FieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  compact?: boolean;
  help?: ReferenceEntry;
  large?: boolean;
  onHelp?: (entry: ReferenceEntry) => void;
};

function Field({ label, value, onChange, compact, help, large, onHelp }: FieldProps) {
  return (
    <label className={large ? 'field large' : compact ? 'field compact' : 'field'}>
      <FieldLabel label={label} help={help} onHelp={onHelp} />
      {large ? (
        <AutoFitInput ariaLabel={label} value={value} onChange={onChange} />
      ) : (
        <input value={value} onChange={(event) => onChange(event.target.value)} />
      )}
    </label>
  );
}

function AutoFitInput({
  ariaLabel,
  value,
  onChange,
}: {
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fontSize, setFontSize] = useState<number>();
  const inputStyle =
    fontSize === undefined
      ? undefined
      : ({ '--fit-font-size': `${fontSize}px` } as CSSProperties & { '--fit-font-size': string });

  useLayoutEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    let frame = 0;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fitText);
    });

    const fitText = () => {
      if (!context) return;

      const styles = window.getComputedStyle(input);
      const availableWidth = Math.max(40, input.clientWidth - 8);
      const rootSize = Number.parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
      const maxSize = Math.min(Math.max(window.innerWidth * 0.034, 1.6 * rootSize), 3.2 * rootSize);
      const minSize = 0.9 * rootSize;
      const fontFamily = styles.fontFamily;
      const fontWeight = styles.fontWeight;
      const fontStyle = styles.fontStyle;
      const text = value || 'Character Name';

      let low = minSize;
      let high = maxSize;
      for (let step = 0; step < 9; step += 1) {
        const mid = (low + high) / 2;
        context.font = `${fontStyle} ${fontWeight} ${mid}px ${fontFamily}`;
        if (context.measureText(text).width <= availableWidth) {
          low = mid;
        } else {
          high = mid;
        }
      }

      setFontSize(Math.floor(low));
    };

    fitText();
    observer.observe(input);
    void document.fonts?.ready.then(fitText);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [value]);

  return (
    <input
      ref={inputRef}
      aria-label={ariaLabel}
      style={inputStyle}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function Textarea({ value, onChange, tall }: { value: string; onChange: (value: string) => void; tall?: boolean }) {
  return <textarea className={tall ? 'tall' : ''} value={value} onChange={(event) => onChange(event.target.value)} />;
}

function Panel({
  title,
  children,
  icon,
  help,
  onHelp,
}: {
  title: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  help?: ReferenceEntry;
  onHelp?: (entry: ReferenceEntry) => void;
}) {
  return (
    <article className="panel ink-card">
      <header>
        {icon && <span className="panel-icon">{icon}</span>}
        <h2>{title}</h2>
        {help && onHelp && (
          <button className="panel-help" type="button" onClick={() => onHelp(help)} aria-label={`${title} help and notes`}>
            <Info size={13} />
          </button>
        )}
      </header>
      {children}
    </article>
  );
}

function CheckRow({
  label,
  value,
  proficient,
  onValueChange,
  onToggleProficiency,
}: {
  label: string;
  value: string;
  proficient: boolean;
  onValueChange: (value: string) => void;
  onToggleProficiency: () => void;
}) {
  return (
    <div className="check-row">
      <button
        className={proficient ? 'sheet-checkbox checked' : 'sheet-checkbox'}
        type="button"
        onClick={onToggleProficiency}
        aria-pressed={proficient}
        aria-label={`Toggle ${label} saving throw proficiency`}
        title="Proficient"
      />
      <input
        className="bonus-input"
        aria-label={`${label} saving throw bonus`}
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
      />
      {label}
    </div>
  );
}

function NumberBadge({
  label,
  value,
  onChange,
  icon,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  icon?: React.ReactNode;
}) {
  return (
    <article className="number-badge ink-card">
      <span>{icon}</span>
      <input aria-label={label} type="number" value={value} onChange={(event) => onChange(Number(event.target.value))} />
      <p>{label}</p>
    </article>
  );
}

function Pips({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  return (
    <div className="pips">
      <span>{label}</span>
      {[1, 2, 3].map((pip) => (
        <button
          className={value >= pip ? 'checked' : ''}
          key={pip}
          type="button"
          onClick={() => onChange(value === pip ? pip - 1 : pip)}
          aria-label={`${label} ${pip}`}
        />
      ))}
    </div>
  );
}

function ReferenceSearch({
  filteredReference,
  search,
  setSearch,
  setSelectedHelp,
}: {
  filteredReference: ReferenceEntry[];
  search: string;
  setSearch: (value: string) => void;
  setSelectedHelp: (entry: ReferenceEntry) => void;
}) {
  const visibleReference = filteredReference.slice(0, 120);

  return (
    <Panel title="Reference Library" icon={<Search size={18} />}>
      <input className="search-input" placeholder="Search rules help" value={search} onChange={(event) => setSearch(event.target.value)} />
      <p className="reference-count">
        {filteredReference.length} {filteredReference.length === 1 ? 'topic' : 'topics'}
      </p>
      <div className="reference-list">
        {visibleReference.map((entry) => (
          <button key={entry.title} type="button" onClick={() => setSelectedHelp(entry)}>
            <span>{entry.title}</span>
            <small>{entry.body.slice(0, 112)}{entry.body.length > 112 ? '...' : ''}</small>
          </button>
        ))}
        {filteredReference.length === 0 && <p className="reference-empty">No matching reference topics.</p>}
      </div>
    </Panel>
  );
}

function HelpDrawer({
  selectedHelp,
  personalNote = '',
  setPersonalNote,
  setSelectedHelp,
}: {
  selectedHelp: ReferenceEntry | null;
  personalNote?: string;
  setPersonalNote?: (value: string) => void;
  setSelectedHelp: (entry: ReferenceEntry | null) => void;
}) {
  if (!selectedHelp) return null;

  return (
    <div className="help-drawer" role="dialog" aria-modal="true" aria-labelledby="help-title">
      <button className="scrim" type="button" onClick={() => setSelectedHelp(null)} aria-label="Close rules help" />
      <article>
        <button className="icon-button close" type="button" onClick={() => setSelectedHelp(null)} aria-label="Close rules help">
          <ChevronDown size={20} />
        </button>
        <h2 id="help-title">{selectedHelp.title}</h2>
        <p>{selectedHelp.body}</p>
        {setPersonalNote && (
          <label className="personal-note">
            <span>My notes for this section</span>
            <textarea
              value={personalNote}
              onChange={(event) => setPersonalNote(event.target.value)}
              placeholder="Add reminders, house rules, table rulings, or character-specific notes."
            />
          </label>
        )}
      </article>
    </div>
  );
}

export { App };
